import * as m from '@staiter/i18n';
import { isOverdueEligible } from '@staiter/shared/engines-pure/state-group-rules';
import type { StateGroup } from '@staiter/shared/task-module';
import type { TaskFilterShared } from '@staiter/shared/task-module';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  CalendarDays,
  ChartGantt,
  Columns3,
  Eye,
  EyeOff,
  FolderOpen,
  Info,
  type LucideIcon,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { EmptyState, ErrorState, Skeleton, SkeletonTable } from '~/components/data';
import { ColumnConfigPopover } from '~/components/data/ColumnConfigPopover';
import { ScopeAllCompaniesCta } from '~/components/domain/ScopeAllCompaniesCta';
import { emitToast } from '~/components/feedback/toast-bus';
import { BulkEditBar } from '~/components/tasks/bulk/BulkEditBar';
import { DisplayPanel } from '~/components/tasks/display/DisplayPanel';
import { ActiveFilterChips } from '~/components/tasks/filters/ActiveFilterChips';
import { ChatFilterChip } from '~/components/tasks/filters/ChatFilterChip';
import { ColumnFilterPopover } from '~/components/tasks/filters/ColumnFilterPopover';
import { FilterBar, type SavedViewLite } from '~/components/tasks/filters/FilterBar';
import type { AdhocRulesState } from '~/components/tasks/filters/FilterCard';
import { GroupByPopover, type GroupByValue } from '~/components/tasks/grouping/GroupByPopover';
import { TaskMenuHost } from '~/components/tasks/menu/TaskMenuHost';
import { useBlankAreaMenu } from '~/components/tasks/menu/useBlankAreaMenu';
import { useTaskSurfaceMenu } from '~/components/tasks/menu/useTaskSurfaceMenu';
import { TaskPeekPanel } from '~/components/tasks/peek/TaskPeekPanel';
import { SaveViewButton } from '~/components/tasks/saved-views/SaveViewButton';
import { SavedViewEmptyRBAC } from '~/components/tasks/saved-views/SavedViewEmptyRBAC';
import { GlobalTableLayout } from '~/components/tasks/views/GlobalTableLayout';
import { type KanbanGroupBy, KanbanLayout } from '~/components/tasks/views/KanbanLayout';
import { ProjectLayoutRoot } from '~/components/tasks/views/ProjectLayoutRoot';
import { WorkspaceSurface } from '~/components/tasks/views/WorkspaceSurface';
import { useChatPanelStore } from '~/lib/chat/chatPanelStore';
import { addIsoDays, businessTodayIso } from '~/lib/tasks/business-day';
import { revalidateListsNow } from '~/lib/tasks/mutations/schedule-list-revalidate';
import { installTaskUndoHotkey } from '~/lib/tasks/mutations/task-undo';
import { useTaskCounts } from '~/lib/tasks/queries/useTaskCounts';
import {
  CALENDAR_COLOR_KEYS,
  type GroupKey,
  dimensionOf,
  resolveGrouping,
} from '~/lib/tasks/selectors/dimensions';
import type { SortKey } from '~/lib/tasks/selectors/grouping';
import { openQuickAdd } from '~/lib/tasks/stores/quickAddStore';
import type { TaskViewType } from '~/lib/tasks/stores/taskViewSwitcherStore';
import { truncationActions } from '~/lib/tasks/truncation-actions';
const CalendarLayout = lazy(() =>
  import('~/components/tasks/views/CalendarLayout').then((mod) => ({
    default: mod.CalendarLayout,
  })),
);
const GanttSurface = lazy(() =>
  import('~/components/tasks/gantt/GanttSurface').then((mod) => ({ default: mod.GanttSurface })),
);
import { Select } from '~/components/primitives';
import {
  FILTER_DRAFT_KEY_RE,
  createFilterDraft,
  fetchFilterDraft,
  isEmptyFilter,
  toDraftBody,
} from '~/lib/tasks/api/filter-drafts';
import { fetchLabels } from '~/lib/tasks/api/labels';
import { createTask, ensureInboxProject, fetchDependenciesByProjects } from '~/lib/tasks/api/tasks';
import { buildSystemFilterFields } from '~/lib/tasks/filters/fields';
import { useZenEscapeExit, useZenSync } from '~/lib/tasks/gantt/useZenMode';
import { listFieldsFor } from '~/lib/tasks/list-fields';
import {
  type TaskListParams,
  taskListQueryOptions,
  useGlobalAssignableUsers,
  useProjectMembers,
  useProjects,
  useStatesForProjects,
  useTasks,
} from '~/lib/tasks/queries/useTasks';
import { fetchAllTaskIds, useTasksSearch } from '~/lib/tasks/queries/useTasksSearch';
import { slaInfo } from '~/lib/tasks/sla';
import { stateGroupOf } from '~/lib/tasks/state-group-of';
import { useTaskFilterStore, useTaskFilterSurface } from '~/lib/tasks/stores/taskFilterStore';
import {
  ACTIVE_STATE_GROUPS,
  PAGE_SIZE_OPTIONS,
  type WorkTasksViewPrefs,
  hasViewPrefs,
  useWorkTasksViewStore,
} from '~/lib/tasks/stores/workTasksViewStore';
import { useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';

/** FE-40 — id колонки таблицы → filterable-поле (per-column фильтр). */
const COLUMN_FILTER_FIELD: Record<string, string> = {
  project: 'project',
  entity: 'entity',
  status: 'status',
  assignee: 'assignee',
  priority: 'priority',
  due: 'due_date',
  start: 'start_date',
};
import { useChatCompositionStore } from '~/lib/chat/compositionModeStore';
import { useTaskSelectionStore } from '~/lib/tasks/stores/taskSelectionStore';
import styles from './_auth.work-tasks.module.css';

/**
 * work-tasks-performance F1 — «загрузка затянулась». Молчащий скелетон на десятой
 * секунде читается как зависание; подпись обязана назвать причину. Флаг поднимается
 * не сразу, чтобы на быстрой выдаче не пугать словом «десять секунд».
 */
function useSlowFlag(active: boolean, ms: number): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!active) {
      setSlow(false);
      return;
    }
    const id = setTimeout(() => setSlow(true), ms);
    return () => clearTimeout(id);
  }, [active, ms]);
  return slow;
}

/**
 * work-tasks-performance F1 — значение hero-чипа: число либо скелетон ТОЙ ЖЕ высоты.
 * Ноль во время загрузки — такая же ложь, как «Нет активных задач»: пользователь читает
 * «просрочек нет», пока сервер ещё считает.
 */
function HeroValue({
  value,
  pending,
  stale,
  atLeast,
  unknown,
  tone,
}: {
  value: number;
  pending: boolean;
  /** Число посчитано по прошлому набору — тон гасится наравне со строками. */
  stale?: boolean;
  /** Выдача обрезана капом: точного числа мы не знаем, знаем нижнюю границу. */
  atLeast?: boolean;
  /** Запрос упал — числа нет вовсе. Ноль здесь был бы ложью, а не значением. */
  unknown?: boolean;
  tone?: string;
}): JSX.Element {
  return (
    <span
      className={[styles.heroValue, pending || stale ? styles.heroStale : (tone ?? '')]
        .filter(Boolean)
        .join(' ')}
      aria-busy={pending || undefined}
    >
      {/* Скелетон декоративен: озвучивать «Загрузка» пятью live-областями подряд —
          шум. Занятость чипа несёт `aria-busy`, подпись остаётся читаемой. */}
      {/* Знак «не меньше» занимает место ВСЕГДА (скрыт `visibility`, а не удалён):
          иначе его появление после ответа прибавлялось к ширине чипа и перекладывало
          весь ряд по горизонтали — на «Без срока» это 33 px уже после того, как чипы
          стали кликабельны (четвёртый прогон гейта фазы A). */}
      <span
        className={styles.heroPrefix}
        aria-hidden="true"
        data-shown={!pending && atLeast && value !== 0 ? 'true' : undefined}
      >
        ≥
      </span>
      {unknown ? (
        <span className={styles.heroUnknown} title={m.tm_count_unavailable_hint()}>
          {m.tm_count_unknown()}
        </span>
      ) : pending ? (
        <Skeleton width={14} height={12} decorative />
      ) : atLeast ? (
        // Полоса «счётчики неполны» и точное число рядом противоречили друг другу
        // (повторный гейт фазы A). «≥» — то, что мы про это число действительно знаем.
        // Ноль под капом не означает «ноль»: он означает «в дошедшей части нет».
        // Знак именно «н/д», а не «—»: прочерком таблица уже обозначает ОТСУТСТВИЕ
        // значения (приоритет, прогресс), и один глиф в двух смыслах на одном экране
        // читался бы как «просроченных нет» (четвёртый прогон гейта фазы A).
        value === 0 ? (
          <span className={styles.heroUnknown} title={m.tm_count_unknown_hint()}>
            {m.tm_count_unknown()}
          </span>
        ) : (
          value
        )
      ) : (
        value
      )}
    </span>
  );
}

/** FE-34/35 — есть ли активный фильтр (пустые rules/match не считаются). */
function isFilterActive(f: TaskFilterShared): boolean {
  return Boolean(
    // R1 — состав, зафиксированный черновиком чата, это тоже фильтр: без него
    // выдача уходила бы мимо `/tasks/search`, а с ней и весь отбор по id.
    f.task_ids ||
      f.q ||
      f.project_ids?.length ||
      f.state_groups?.length ||
      f.priorities?.length ||
      f.subject_types?.length ||
      f.assignee_user_ids?.length ||
      f.label_ids?.length ||
      f.no_assignee ||
      f.due_from ||
      f.due_to ||
      f.overdue_only ||
      f.has_dates !== undefined ||
      f.custom_field_key ||
      f.rules?.length,
  );
}

/**
 * `/work-tasks` — глобальный inbox задач (FE-S12.2-22).
 *
 * Namespace `work-tasks` — coexistence c legacy `/projects` (S17-S22).
 * SearchParams через Zod (groupBy + project filter).
 * Full-width: проекты здесь — ПРОСТО фильтр (project_ids в adhocFilter), не
 * навигация. Смотреть все проекты — отдельный дэшборд `/projects`. RBAC-урезанный
 * вид → SavedViewEmptyRBAC.
 */

/**
 * WTU-FND-L — параметры адресной строки НЕ роняют экран.
 *
 * До правки поле `group` разбиралось без `.catch()`, и `/work-tasks?group=status`
 * (правильный ключ — `state`) валил ВСЁ приложение в полноэкранное «Что-то пошло
 * не так» с пустым trace: `SearchParamError` — клиентское исключение, запроса на
 * бэкенд не было, и в логах сервера искать нечего. Достаточно устаревшей
 * закладки или ссылки, набранной руками.
 *
 * Правило теперь одно на все поля: **непонятое значение параметра откатывается к
 * умолчанию, экран остаётся живым**. Адресная строка — это ввод от пользователя,
 * а ввод не должен валить приложение; частичная стойкость (`.catch()` у `vt` и
 * `zen`, но не у соседей того же объекта) — худший вид, потому что создаёт
 * ложное впечатление, что класс закрыт.
 *
 * Барьер: `scripts/lint/fe-search-params-catch.mjs` (в `pnpm lint:guards`).
 */
const SearchSchema = z.object({
  project: z.string().uuid().optional().catch(undefined),
  group: z
    .enum([
      'project',
      'entity',
      'state',
      // FE-18 — группа статусов как самостоятельная ось: кросс-проектная доска
      // строит колонки по шести группам, а не по N×статусам всех проектов.
      'state_group',
      'due',
      'priority',
      'assignee',
      'author',
      'label',
      'hierarchy',
      // Легаси-ключи из старых ссылок: 'folder' → 'hierarchy', 'urgency' → 'due'.
      'folder',
      'urgency',
      'none',
    ])
    .optional()
    .catch(undefined),
  /** vt — тип представления глобального дэшборда (TD-2/3/5). FE-3: «Список» удалён,
   *  дефолт — Таблица; старые `?vt=list` URL мягко падают на 'table' через `.catch`. */
  vt: z.enum(['table', 'board', 'calendar', 'gantt']).catch('table').optional(),
  /** sort — сортировка внутри групп (TD-4; created/updated — TD-8-FE-3, данные TD-16). */
  sort: z
    .enum(['manual', 'due_asc', 'due_desc', 'priority', 'title', 'created', 'updated'])
    .optional()
    .catch(undefined),
  /** show_empty — показывать пустые группы (TD-8 Display → потребляется TD-9). */
  show_empty: z.boolean().optional().catch(undefined),
  /** collapsed — свёрнутые группы (TD-9), comma-joined keys. */
  collapsed: z.string().optional().catch(undefined),
  /** hideprops — скрытые колонки таблицы (TD-11 display_properties), comma-joined. */
  hideprops: z.string().optional().catch(undefined),
  /** colorder — порядок колонок таблицы (FE-38), comma-joined id движимых колонок. */
  colorder: z.string().optional().catch(undefined),
  /** colw — ширины колонок (FE-39), comma-joined `id:width`. */
  colw: z.string().optional().catch(undefined),
  /** subgroup — подгруппировка Доски (TD-14-FE-2 swimlanes). */
  subgroup: z
    .enum(['state', 'assignee', 'priority', 'due', 'label', 'none'])
    .optional()
    .catch(undefined),
  peek: z.string().uuid().optional().catch(undefined),
  /** Группа П (§6) — полноэкранный zen-режим канваса ганта. `?zen=1`: deep-link,
   *  reload сохраняет, browser back выходит. Значение не персистится в сторах. */
  zen: z
    .union([z.literal(1), z.literal(true)])
    .optional()
    .catch(undefined),
  /**
   * F49 (CIA-F1) — ключ черновика фильтра (B44). Сам фильтр в адрес не помещается
   * (до 50 правил при пределе строки 8 КБ), поэтому в адресе — только ключ.
   */
  f: z.string().regex(FILTER_DRAFT_KEY_RE).optional().catch(undefined),
});

type SearchParams = z.infer<typeof SearchSchema>;

/** F49 — пауза после правки фильтра перед записью черновика: не ключ на каждый символ. */
const FILTER_DRAFT_DEBOUNCE_MS = 700;

/** TD-16 — доп. колонки скрыты по умолчанию (не раздувают таблицу);
 *  756 — «Постановщик» тоже opt-in (роль видна в карточке, в таблице по запросу). */
const DEFAULT_HIDDEN_PROPS = ['start', 'created', 'updated', 'author'];

/** column-prefs (2026-07-09) — персист конфига колонок между сессиями (см. эффекты ниже). */
const COLUMN_PREFS_KEY = 'staiter.work-tasks.columns';

/** TD-2/3/5 — тип представления глобального дэшборда. */
type ViewTabValue = 'table' | 'board' | 'calendar' | 'gantt';
type ViewTab = { key: ViewTabValue; label: string; icon: LucideIcon };

/** Дашборд называет виды иначе, чем проектный макет ('table'/'board' против
 *  'list'/'kanban'). Реестр измерений знает один словарь — переводим на него здесь,
 *  а не разводим два набора правил. */
const VIEW_TO_TASK_VIEW: Record<ViewTabValue, TaskViewType> = {
  table: 'list',
  board: 'kanban',
  calendar: 'calendar',
  gantt: 'gantt',
};

export const Route = createFileRoute('/_auth/work-tasks/')({
  staticData: { minMainWidth: 792 },
  validateSearch: SearchSchema,
  component: WorkTasksInbox,
});

/**
 * work-tasks-performance F14/B11 — клиентские ключи сортировки → серверные.
 *
 * На странице клиент больше не видит весь набор, а значит не может отсортировать
 * «первые N»: их выбирает сервер, и порядок обязан задавать он же. Ключи, которых
 * в whitelist сервера нет (`manual` — ручной порядок), не отправляются вовсе.
 */
/**
 * Подписи порядка строк — для оговорки «первые N из M».
 *
 * Гейт фазы D: «первые 500» без названного порядка не даёт пользователю
 * рассуждать, чего именно он не видит. Словарь тот же, что в панели «Вид»
 * (`DisplayPanel`), — заводить второй набор подписей нельзя, разъедутся.
 */
const SORT_LABEL: Record<SortKey, () => string> = {
  manual: () => m.tm_manual(),
  due_asc: () => m.tm_sort_due_asc(),
  due_desc: () => m.tm_sort_due_desc(),
  priority: () => m.tm_priority(),
  title: () => m.tm_sort_name_az(),
  created: () => m.tm_display_sort_created(),
  updated: () => m.tm_display_sort_updated(),
};

/** Максимальный размер страницы таблицы — верх списка `PAGE_SIZE_OPTIONS`. */
const MAX_PAGE_SIZE = Math.max(...PAGE_SIZE_OPTIONS);

const SERVER_SORT: Record<string, { sortBy: string; sortDir: 'asc' | 'desc' } | undefined> = {
  manual: undefined,
  due_asc: { sortBy: 'due_date', sortDir: 'asc' },
  due_desc: { sortBy: 'due_date', sortDir: 'desc' },
  priority: { sortBy: 'priority', sortDir: 'asc' },
  title: { sortBy: 'title', sortDir: 'asc' },
  created: { sortBy: 'created_at', sortDir: 'desc' },
  updated: { sortBy: 'updated_at', sortDir: 'desc' },
};

function WorkTasksInbox(): JSX.Element {
  const {
    project,
    group,
    vt,
    sort,
    show_empty,
    collapsed,
    hideprops,
    colorder,
    colw,
    subgroup,
    peek,
    zen,
    f: filterKey,
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Группа П (§6) — zen из search-param. Синхронизируем ui-флаг + Esc-выход (проверка порталов).
  const zenActive = zen === 1 || zen === true;
  const toggleZen = useCallback(
    () => navigate({ search: (prev) => ({ ...prev, zen: prev.zen ? undefined : 1 }) }),
    [navigate],
  );
  const exitZen = useCallback(
    () => navigate({ search: (prev) => ({ ...prev, zen: undefined }) }),
    [navigate],
  );
  useZenSync(zenActive);
  useZenEscapeExit(zenActive, exitZen);
  const queryClient = useQueryClient();
  // task-quick-capture F2 — inline-захват: ввод задачи в пустом состоянии создаёт её во
  // «Входящих» (ensureInbox → createTask) и открывает доску инбокса для продолжения.
  const [quickTitle, setQuickTitle] = useState('');
  const quickAddRef = useRef<HTMLInputElement>(null);
  const quickAddMut = useMutation({
    mutationFn: async (title: string) => {
      const p = await ensureInboxProject();
      await createTask({ project_id: p.id, title, kind: 'task' });
      return p.id;
    },
    onSuccess: (projectId) => {
      setQuickTitle('');
      // work-tasks-performance F10 — точечно; см. QuickAddTask.
      revalidateListsNow(queryClient);
      navigate({ to: '/work-tasks/$projectId', params: { projectId } });
    },
  });
  // TD-14-FE-2 — подгруппировка Доски (swimlanes) + локальный collapse дорожек.
  const setSubGroup = (v: string) =>
    navigate({
      search: (prev) => ({ ...prev, subgroup: v === 'none' ? undefined : (v as never) }),
    });
  const [collapsedSubGroups, setCollapsedSubGroups] = useState<Set<string>>(new Set());
  const toggleSubGroup = (key: string) =>
    setCollapsedSubGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // column-prefs (2026-07-09) — «настройки столбцов слетают периодически»: весь конфиг
  // колонок жил ТОЛЬКО в URL (colorder/hideprops/colw) — любой заход без параметров
  // (клик «Задачи» в сайдбаре, deep-link, редирект при смене компании) сбрасывал его.
  // localStorage — восстановление при пустом URL; URL остаётся источником правды
  // (шаринг ссылки с чужим конфигом уважается и перезаписывает сохранённый).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only restore — только при заходе без параметров
  useEffect(() => {
    if (colorder !== undefined || hideprops !== undefined || colw !== undefined) return;
    try {
      const raw = localStorage.getItem(COLUMN_PREFS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { colorder?: string; hideprops?: string; colw?: string };
      if (!saved.colorder && saved.hideprops === undefined && !saved.colw) return;
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          ...(saved.colorder ? { colorder: saved.colorder } : {}),
          ...(saved.hideprops !== undefined ? { hideprops: saved.hideprops } : {}),
          ...(saved.colw ? { colw: saved.colw } : {}),
        }),
      });
    } catch {
      // повреждённый JSON / приватный режим — игнор
    }
  }, []);
  useEffect(() => {
    if (colorder === undefined && hideprops === undefined && colw === undefined) return;
    try {
      localStorage.setItem(
        COLUMN_PREFS_KEY,
        JSON.stringify({
          ...(colorder !== undefined ? { colorder } : {}),
          ...(hideprops !== undefined ? { hideprops } : {}),
          ...(colw !== undefined ? { colw } : {}),
        }),
      );
    } catch {
      // квота/приватный режим — игнор
    }
  }, [colorder, hideprops, colw]);
  // TD-11/16 — скрытые колонки таблицы в URL; absent → доп.колонки скрыты по умолчанию.
  const hiddenProps = useMemo(
    () =>
      hideprops === undefined
        ? new Set(DEFAULT_HIDDEN_PROPS)
        : new Set(hideprops.split(',').filter(Boolean)),
    [hideprops],
  );
  const toggleProperty = (key: string) =>
    navigate({
      search: (prev) => {
        const cur =
          prev.hideprops === undefined
            ? new Set(DEFAULT_HIDDEN_PROPS)
            : new Set(prev.hideprops.split(',').filter(Boolean));
        if (cur.has(key)) cur.delete(key);
        else cur.add(key);
        // Всегда пишем (даже пусто) — иначе absent сбросит default-hidden extras.
        return { ...prev, hideprops: [...cur].join(',') };
      },
    });
  // FE-38 — порядок колонок таблицы в URL (id движимых, без 'title').
  const columnOrder = useMemo(
    () => (colorder ? colorder.split(',').filter(Boolean) : []),
    [colorder],
  );
  const setColumnOrder = (ids: string[]) =>
    navigate({ search: (prev) => ({ ...prev, colorder: ids.join(',') || undefined }) });
  const resetColumns = () => {
    // column-prefs — «Сбросить» чистит и сохранённый конфиг, не только URL.
    try {
      localStorage.removeItem(COLUMN_PREFS_KEY);
    } catch {
      // ignore
    }
    navigate({
      search: (prev) => ({ ...prev, colorder: undefined, hideprops: undefined, colw: undefined }),
    });
  };
  // FE-39 — ширины колонок: URL `colw` = id:width,…. null ширина → auto-fit (удалить).
  const columnWidths = useMemo(() => {
    const m = new Map<string, number>();
    for (const pair of (colw ?? '').split(',').filter(Boolean)) {
      const [id, w] = pair.split(':');
      const n = Number(w);
      if (id && Number.isFinite(n)) m.set(id, n);
    }
    return m;
  }, [colw]);
  const setColumnWidth = (id: string, width: number | null) =>
    navigate({
      search: (prev) => {
        const m = new Map<string, number>();
        for (const pair of (prev.colw ?? '').split(',').filter(Boolean)) {
          const [pid, pw] = pair.split(':');
          const n = Number(pw);
          if (pid && Number.isFinite(n)) m.set(pid, n);
        }
        if (width === null) m.delete(id);
        else m.set(id, width);
        const next = [...m].map(([k, v]) => `${k}:${v}`).join(',');
        return { ...prev, colw: next || undefined };
      },
    });
  // tasks-by-company-fixes T4 — «Компания» как тоггл-able свойство в «Колонки» (только агрегат).
  // GlobalTableLayout уже уважает hiddenProps для entity-колонки → достаточно добавить её в список.
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  // i18n: лейблы колонок строятся в теле компонента (не на module-level), чтобы m.*()
  // не заморозил локаль на импорте. TD-11/16 — переключаемые колонки; 'title' всегда видна.
  const TABLE_PROPERTIES = useMemo<ReadonlyArray<{ key: string; label: string }>>(
    () => [
      { key: 'project', label: m.tm_project() },
      { key: 'status', label: m.tm_status() },
      { key: 'assignee', label: m.tm_route_col_executor() },
      { key: 'author', label: m.tm_author() },
      { key: 'priority', label: m.tm_priority() },
      { key: 'due', label: m.tm_due() },
      { key: 'labels', label: m.tm_labels() },
      { key: 'sla', label: m.tm_risk() },
      { key: 'start', label: m.tm_start() },
      { key: 'created', label: m.tm_route_col_created() },
      { key: 'updated', label: m.tm_route_col_updated() },
    ],
    [],
  );
  const tableProperties = useMemo<ReadonlyArray<{ key: string; label: string }>>(
    () =>
      aggregateScope
        ? TABLE_PROPERTIES.flatMap((p, i) =>
            i === 0 ? [p, { key: 'entity', label: m.project_col_entity() }] : [p],
          )
        : TABLE_PROPERTIES,
    [aggregateScope, TABLE_PROPERTIES],
  );
  // FE-38 — дескрипторы колонок в текущем порядке для ColumnConfigPopover.
  const columnDescriptors = useMemo(() => {
    const moved = columnOrder.length
      ? [
          ...columnOrder
            .map((id) => tableProperties.find((p) => p.key === id))
            .filter((p): p is { key: string; label: string } => p !== undefined),
          ...tableProperties.filter((p) => !columnOrder.includes(p.key)),
        ]
      : tableProperties;
    return [
      { id: 'title', label: m.tm_route_col_task(), pinned: true },
      ...moved.map((p) => ({ id: p.key, label: p.label })),
    ];
  }, [columnOrder, tableProperties]);
  const visibleColumnIds = useMemo(
    () => new Set(tableProperties.map((p) => p.key).filter((k) => !hiddenProps.has(k))),
    [hiddenProps, tableProperties],
  );
  // TD-9 — свёрнутые группы в URL (общий механизм для Таблицы и Списка).
  const collapsedGroups = useMemo(
    () => new Set((collapsed ?? '').split(',').filter(Boolean)),
    [collapsed],
  );
  const toggleGroup = (key: string) =>
    navigate({
      search: (prev) => {
        const cur = new Set((prev.collapsed ?? '').split(',').filter(Boolean));
        if (cur.has(key)) cur.delete(key);
        else cur.add(key);
        const next = [...cur].join(',');
        return { ...prev, collapsed: next || undefined };
      },
    });
  // i18n: табы представлений строятся в теле компонента (иконки статичны, лейблы — m.*()).
  const VIEW_TABS = useMemo<ReadonlyArray<ViewTab>>(
    () => [
      { key: 'table', label: m.tm_route_view_table(), icon: Table2 },
      { key: 'board', label: m.tm_route_view_board(), icon: Columns3 },
      { key: 'calendar', label: m.tm_route_view_calendar(), icon: CalendarDays },
      { key: 'gantt', label: m.tm_route_view_gantt(), icon: ChartGantt },
    ],
    [],
  );
  const displayView = vt ?? 'table';
  const setDisplayView = (v: ViewTabValue) =>
    navigate({ search: (prev) => ({ ...prev, vt: v === 'table' ? undefined : v }) });
  const sortBy: SortKey = sort ?? 'manual';
  const setSortBy = (s: SortKey) =>
    navigate({ search: (prev) => ({ ...prev, sort: s === 'manual' ? undefined : s }) });
  const showEmpty = show_empty ?? false;
  const setShowEmpty = (v: boolean) =>
    navigate({ search: (prev) => ({ ...prev, show_empty: v ? true : undefined }) });
  const projectsQuery = useProjects();
  // chat-interactive-actions F1 (I5) — фильтр переехал из `useState` в общий стор:
  // у React-состояния нет входа снаружи, а инструмент чата обязан задавать ровно
  // тот же фильтр, что и карточка фильтра. Локальный сеттер сохранён по форме
  // (`setAdhocFilter(next | updater)`), чтобы весь остальной экран не переписывать.
  const filterSurface = useTaskFilterSurface('global');
  const adhocFilter = filterSurface.filter;
  const setFilterInStore = useTaskFilterStore((st) => st.setFilter);
  const setAdhocFilter = useCallback(
    (next: TaskFilterShared | ((prev: TaskFilterShared) => TaskFilterShared)) => {
      const prev = useTaskFilterStore.getState().getSurface('global').filter;
      setFilterInStore('global', typeof next === 'function' ? next(prev) : next);
    },
    [setFilterInStore],
  );
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');

  /*
   * F49 (CIA-F1) — фильтр переживает перезагрузку и уходит ссылкой. Правка фильтра
   * (после паузы) кладёт его черновиком на сервер, ключ встаёт в адрес `?f=`;
   * заход с ключом восстанавливает фильтр. Ключ, который сервер не отдаёт (истёк
   * через 30 дней, чужой), снимается из адреса с сообщением — молча показать «все
   * задачи» вместо присланной выборки значило бы соврать.
   *
   * `appliedKeyRef` — ключ, чей фильтр сейчас на экране: свой же ключ, поставленный
   * в адрес после записи, повторно не читается. `savedBodyRef` — тело фильтра, уже
   * лежащее под ключом в адресе: без него восстановление тут же записало бы копию.
   */
  const appliedKeyRef = useRef<string | null>(null);
  const savedBodyRef = useRef<string>(JSON.stringify({}));
  const draftSeqRef = useRef(0);
  const [restoringFilter, setRestoringFilter] = useState(filterKey !== undefined);
  useEffect(() => {
    if (!filterKey || filterKey === appliedKeyRef.current) {
      setRestoringFilter(false);
      return;
    }
    let cancelled = false;
    setRestoringFilter(true);
    fetchFilterDraft(filterKey)
      .then((restored) => {
        if (cancelled) return;
        appliedKeyRef.current = filterKey;
        if (restored) {
          savedBodyRef.current = JSON.stringify(toDraftBody(restored));
          setAdhocFilter(restored);
          return;
        }
        emitToast({ variant: 'warning', title: m.tm_filter_link_expired() });
        appliedKeyRef.current = null;
        navigate({ search: (prev) => ({ ...prev, f: undefined }), replace: true });
      })
      .catch(() => {
        // Сеть/сервер — фильтр не восстановлен, но ключ в адресе остаётся: повторный
        // заход прочитает его снова. Ошибку запроса показывает общий обработчик.
      })
      .finally(() => {
        if (!cancelled) setRestoringFilter(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterKey, navigate, setAdhocFilter]);
  const adhocFilterBody = JSON.stringify(toDraftBody(adhocFilter));
  useEffect(() => {
    if (restoringFilter || adhocFilterBody === savedBodyRef.current) return;
    const seq = ++draftSeqRef.current;
    const timer = setTimeout(() => {
      const current = useTaskFilterStore.getState().getSurface('global').filter;
      if (isEmptyFilter(current)) {
        savedBodyRef.current = adhocFilterBody;
        appliedKeyRef.current = null;
        navigate({ search: (prev) => ({ ...prev, f: undefined }), replace: true });
        return;
      }
      createFilterDraft(current)
        .then((key) => {
          // Ответ на устаревшую правку адрес не трогает: его перепишет последняя.
          if (seq !== draftSeqRef.current) return;
          savedBodyRef.current = adhocFilterBody;
          appliedKeyRef.current = key;
          navigate({ search: (prev) => ({ ...prev, f: key }), replace: true });
        })
        .catch(() => {
          // Ключ не записан — фильтр на экране цел, в адресе остаётся прежний ключ.
        });
    }, FILTER_DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [adhocFilterBody, restoringFilter, navigate]);

  // FE-34 — активный сохранённый вид + снимок фильтра для dirty-индикатора.
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [appliedSnapshot, setAppliedSnapshot] = useState<string>('');

  // FE-35 — rule-состояние фильтр-карточки (match + правила) поверх одного adhocFilter.
  const rulesState: AdhocRulesState = {
    match: adhocFilter.match ?? 'all',
    rules: adhocFilter.rules ?? [],
  };
  const onRulesChange = (next: AdhocRulesState) =>
    setAdhocFilter((f) => ({ ...f, match: next.match, rules: next.rules }));
  // FE-34 — текст-поиск задач (orthogonal фильтрам), пишет в adhocFilter.q.
  const setSearchText = (q: string) => setAdhocFilter((f) => ({ ...f, q: q || undefined }));
  const applyView = (view: SavedViewLite) => {
    const f = (view.filter ?? {}) as TaskFilterShared;
    setAdhocFilter(f);
    setActiveViewId(view.id);
    setAppliedSnapshot(JSON.stringify(f));
  };
  const clearView = () => {
    setActiveViewId(null);
    setAppliedSnapshot('');
    setAdhocFilter({});
  };
  const viewDirty = activeViewId !== null && JSON.stringify(adhocFilter) !== appliedSnapshot;

  // F1 [П2] — запись ИЗВНЕ (агент) эквивалентна `clearView()`: сохранённый вид
  // после чужой правки перестаёт быть применённым, и «вид изменён» на нём — ложь.
  // Реагируем на `fromChat` + рост версии, а не на сам фильтр: ручные правки
  // dirty-индикатор обязан оставлять как есть.
  const chatWriteVersion = filterSurface.fromChat ? filterSurface.version : 0;
  const seenChatVersion = useRef(0);
  useEffect(() => {
    if (chatWriteVersion === 0 || chatWriteVersion === seenChatVersion.current) return;
    seenChatVersion.current = chatWriteVersion;
    setActiveViewId(null);
    setAppliedSnapshot('');
  }, [chatWriteVersion]);

  /**
   * F18 [Х14–Х16, Х18] — подводка состава после «Открыть задачи в списке».
   *
   * Подсветки строк здесь НЕТ и не будет: фильтр показывает только состав, и
   * признак стоял бы на ста процентах строк — он не отличал бы ничего. Смысл
   * «это набор из чата» уже несёт чип над таблицей. Недостающее — другое:
   * набор может начинаться ниже видимой части, а колонка, которую операция
   * меняет, при узком поле уезжает за правый край. Подводим обе.
   *
   * Строка берётся ПЕРВАЯ В ПОРЯДКЕ ТАБЛИЦЫ (`items[0]`), а не первая в составе:
   * состав приходит с сервера в порядке «сначала разрешённые, потом отказанные»,
   * и прыжок к нему выглядел бы случайным.
   *
   * Фокус НЕ забираем: человек продолжает читать карточку и печатать в чат.
   */
  /**
   * F19 — режим состава: панель массовых действий сворачивается, пока
   * выделением распоряжается предложение чата.
   */
  const chatComposition = useChatCompositionStore();
  const chatCompositionIds = useMemo(
    () => new Set(chatComposition.taskIds),
    [chatComposition.taskIds],
  );
  const selectedIdsForComposition = useTaskSelectionStore((st) => st.selectedTaskIds);
  /*
   * R25 (F8 пака `tails-closeout`, В-20): задача быстрого просмотра — в общий стор,
   * строка состава с этим id подсвечивается как «проверяемая сейчас». Уходя с
   * маршрута, снимаем: подсветка без открытого просмотра была бы ложной.
   */
  const setChatReviewing = chatComposition.setReviewing;
  useEffect(() => {
    setChatReviewing(peek ?? null);
    return () => setChatReviewing(null);
  }, [peek, setChatReviewing]);

  const chatRevealApi = useRef<{
    scrollToTask: (taskId: string, opts?: { focus?: boolean }) => boolean;
    revealColumn: (columnId: string) => boolean;
  } | null>(null);
  const revealedChatVersion = useRef(0);

  // Проекты здесь — ПРОСТО фильтр (project_ids), а не навигация. Всё через adhocFilter,
  // синхронно с фильтр-карточкой. FE-2: ряд проект-чипсов удалён — фильтр по проекту
  // только из фильтр-карточки.
  /*
   * F44 (WTU-FND-D2) — режим «только выбранные»: отбор по id выделения. Выделение
   * переживает страницы, и выбранные вне экрана становятся видимы этим режимом, а
   * не остаются утверждением о невидимом. Сам фильтр карточки не трогается.
   */
  const [showSelectedIds, setShowSelectedIds] = useState<string[] | null>(null);
  const activeFilter = showSelectedIds
    ? { ...adhocFilter, task_ids: showSelectedIds }
    : adhocFilter;
  const hasFilter = isFilterActive(activeFilter);
  // work-tasks-defaults F1/F4 — дефолт скрывает закрытые (completed+cancelled) + размер страницы.
  // showClosed/pageSize — общий стор (глоб+проект). activeOnly — вычисляемая производная ВНЕ
  // adhocFilter: hasFilter-семантика не меняется. Снимается тумблером или явным статус-фильтром.
  // F4 [П10] — эфемерный агентский показ закрытых поверх пользовательского
  // предпочтения: pref в localStorage агент не переписывает, иначе «покажи
  // закрытые» осталось бы включённым навсегда и без следа, кто это сделал.
  const showClosedPref = useWorkTasksViewStore((s) => s.showClosed);
  const showClosed = filterSurface.showClosedOverride ?? showClosedPref;
  const resetChatFilter = useCallback(() => {
    useTaskFilterStore.getState().resetFilter('global');
    setActiveViewId(null);
    setAppliedSnapshot('');
  }, []);
  // F18 — архивные показываются по явному запросу: это не «ещё один статус»,
  // а убранные с глаз записи, и по умолчанию их в списке быть не должно.
  const showArchived = useWorkTasksViewStore((s) => s.showArchived);
  const setShowArchived = useWorkTasksViewStore((s) => s.setShowArchived);
  const setShowClosed = useWorkTasksViewStore((s) => s.setShowClosed);
  const pageSize = useWorkTasksViewStore((s) => s.pageSize);
  const setPageSize = useWorkTasksViewStore((s) => s.setPageSize);
  /**
   * FND-17 — постраничная навигация по выдаче.
   *
   * Ручка списка курсор УМЕЛА с самого начала (`cursor` в `fetchTasks`,
   * `next_cursor` в ответе), но потребителя у него не было: на экране висело
   * «показано 500 из 1472», страница уже максимальна, и единственным путём к
   * остальным задачам было сузить фильтр. То есть тысяча задач была недостижима
   * из интерфейса вообще.
   *
   * Стек, а не один курсор: курсорная выдача однонаправленная — «назад» нельзя
   * вычислить, его можно только помнить. `cursorStack[i]` — курсор, с которого
   * начинается страница `i+1`; пустой стек = первая страница.
   */
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const userPickedStatus = (adhocFilter.rules ?? []).some((r) => r.field === 'status');
  // F44 — в режиме «только выбранные» закрытые не прячутся: выбрано — значит видно.
  const activeOnly = !showClosed && !userPickedStatus && !showSelectedIds;
  /*
   * F48 (WTP-B3) — таблица просит у сервера только поля видимых колонок: метки
   * объектами и пользовательские поля не ездят, пока их не показывают. Гант, доска
   * и календарь рисуют строку целиком — им набор полей не сужается.
   */
  const tableFields =
    displayView === 'table'
      ? listFieldsFor({
          labels: !hiddenProps.has('labels') || group === 'label',
          customFields: (colorder ?? '')
            .split(',')
            .some((id) => id.startsWith('cf:') && !hiddenProps.has(id)),
        })
      : undefined;
  const searchQuery = useTasksSearch({
    // state_groups подмешивается В ЗАПРОС, НЕ в adhocFilter (чипы/saved-view/hasFilter целы).
    filter: activeOnly ? { ...activeFilter, state_groups: [...ACTIVE_STATE_GROUPS] } : activeFilter,
    limit: pageSize,
    ...(tableFields ? { fields: tableFields } : {}),
    enabled: hasFilter,
  });
  // FE-38 — полная выдача (view=gantt, кап 2000 на бэкенде) нужна не только ганту.
  // Вся модель группировок — клиентская: состав групп и счётчики считаются по тому, что
  // доехало. На обрезанной сотне «Проект A · 12» означало бы 12 из сорока — уверенно и
  // неверно. Поэтому при активной группировке тоже просим полный набор.
  //
  // Почему не склейка страниц по курсору: она даёт свой класс багов (дубли и пропуски на
  // равных sort_order, «хвост» чужих страниц при смене оси во время подгрузки), а кап на
  // бэкенде всё равно остаётся. Обрезка по капу видна пользователю — бейдж ниже.
  // work-tasks-defaults F1/F4 (rework после design-review MAJOR «числа врут») — no-filter путь
  // ВСЕГДА грузит полный активный набор (view=gantt, кап 2000): hero- и групповые счётчики
  // точны по ВСЕЙ выдаче, а не по странице. page-size стал клиентским display-слайсом плоской
  // таблицы (ниже), а не серверным лимитом — иначе счётчики считались бы по обрезанной сотне.
  /**
   * work-tasks-performance F14 — таблица качает СТРАНИЦУ, остальные виды — набор.
   *
   * Раньше все виды просили `view=gantt` (кап 2000) даже ради показа сотни строк:
   * весь набор ехал по сети и разбирался на главном потоке ради одной страницы.
   * Теперь таблица просит ровно `pageSize`, а числа (hero, группы) берёт из
   * `/tasks/counts` — иначе они описывали бы страницу, называясь общими.
   *
   * Гант, доска и календарь остаются на полном наборе (не-цель §6.1): у них
   * своя геометрия — дерево связей, колонки, сетка месяца — и страница их
   * ломает, а не ускоряет.
   */
  /**
   * F14/FND-14 — когда таблица имеет право просить СТРАНИЦУ.
   *
   * Без группировки — всегда: числа hero даёт `/tasks/counts`. С группировкой
   * заголовок обязан называть всю группу, а не её видимую часть, и такое число
   * есть только у оси «проект» (`by_primary_project`, тот же grain, что у
   * `groupByProject`). Для осей «исполнитель», «метка», «приоритет», «срок»
   * серверного разреза нет — там таблица по-прежнему берёт полный набор, иначе
   * заголовки начали бы врать. Это ограничение, а не забывчивость.
   */
  const groupedPagedAxis = group === 'project';
  const tablePagedMode =
    displayView === 'table' && (group === 'none' || (groupedPagedAxis && !hasFilter));
  const baseListParams: TaskListParams = {
    ...(project ? { projectId: project } : {}),
    ...(activeOnly ? { stateGroups: [...ACTIVE_STATE_GROUPS] } : {}),
    ...(showArchived ? { includeArchived: true } : {}),
  };
  /** Параметры страницы (таблица) и полного набора (гант/доска/календарь). */
  /**
   * FND-17 — смена условий выдачи возвращает на первую страницу.
   *
   * Курсор указывает на позицию В КОНКРЕТНОМ наборе. Сменили фильтр, сортировку,
   * группировку, размер страницы или проект — набор другой, и старый курсор
   * означает произвольное место в нём: экран показал бы «страницу 3» набора,
   * которого больше нет. Молча, потому что строки-то придут.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: эффект реагирует на условия выдачи, а не на сам стек — иначе зациклится
  useEffect(() => {
    setCursorStack((prev) => (prev.length === 0 ? prev : []));
  }, [project, group, sort, pageSize, showArchived, activeOnly, JSON.stringify(activeFilter)]);

  /**
   * Раунд 2 гейта, UX-2 — смена страницы СНИМАЕТ выделение.
   *
   * Выделение, пережившее переход, — это отмеченные строки, которых на экране
   * нет: человек жмёт массовое действие, а оно применяется к невидимому.
   * Подпись «выбрано N из загруженных» про это не скажет, потому что «загружено»
   * после перехода уже другое. Снятие — предсказуемое поведение, которое можно
   * объяснить одной фразой; сохранение — нельзя.
   */
  const clearSelection = useTaskSelectionStore((st) => st.clearSelection);
  const selectedCount = useTaskSelectionStore((st) => st.selectedTaskIds.size);
  /*
   * F44 (WTU-FND-D2, H3) — выделение ПЕРЕЖИВАЕТ смену страницы. Раньше переход
   * снимал его (раунд 2 гейта: «отмеченные строки, которых на экране нет»), а
   * статичная строка «Переход снимет выделение» читалась статусом подвала.
   * Теперь невидимое выделение видно: панель называет, сколько выбранных вне
   * экрана, и показывает их режимом «только выбранные».
   */
  const selectAllTasks = useTaskSelectionStore((st) => st.selectAll);
  const selectedIdsNow = useTaskSelectionStore((st) => st.selectedTaskIds);

  const pageCursor = cursorStack.at(-1);
  const pagedListParams: TaskListParams = {
    ...baseListParams,
    ...(tableFields ? { fields: tableFields } : {}),
    limit: pageSize,
    includeTotal: false,
    ...(pageCursor ? { cursor: pageCursor } : {}),
    ...(SERVER_SORT[sort ?? 'manual'] ?? {}),
  };
  const fullListParams: TaskListParams = {
    ...baseListParams,
    view: 'gantt' as const,
    ...(tableFields ? { fields: tableFields } : {}),
  };
  const tasksQuery = useTasks(tablePagedMode ? pagedListParams : fullListParams);

  /**
   * FND-15 — прогрев набора целевого вида. Ad-hoc фильтр сюда не попадает: там
   * экран питает поиск, и у видов один ключ на всех — греть нечего.
   */
  const prefetchView = (target: ViewTabValue): void => {
    if (hasFilter || target === displayView) return;
    const targetPaged = target === 'table' && (group === 'none' || groupedPagedAxis);
    void queryClient.prefetchQuery(
      taskListQueryOptions(targetPaged ? pagedListParams : fullListParams),
    );
  };

  /**
   * F14 — числа hero-чипов и заголовков групп с сервера (B10).
   *
   * При активном ad-hoc фильтре ручка счётчиков не используется: она знает
   * фасеты списка, но не произвольные правила фильтра — а число, посчитанное по
   * другому набору, хуже отсутствующего.
   */
  const countsQuery = useTaskCounts({
    ...(project ? { projectId: project } : {}),
    ...(activeOnly ? { stateGroups: [...ACTIVE_STATE_GROUPS] } : {}),
    ...(showArchived ? { includeArchived: true } : {}),
    enabled: !hasFilter,
  });
  /** Числа с сервера доступны, пока не активен ad-hoc фильтр (см. выше). */
  const serverCounts = hasFilter ? undefined : countsQuery.data;
  /**
   * FND-14 — полные числа групп для заголовков. Только в постраничном режиме и
   * только по оси «проект»: на полном наборе заголовок и так считает по своим
   * строкам, и второе число рядом было бы шумом.
   */
  const groupTotals = useMemo(() => {
    if (!tablePagedMode || !groupedPagedAxis || !serverCounts) return undefined;
    return new Map(Object.entries(serverCounts.by_primary_project));
  }, [tablePagedMode, groupedPagedAxis, serverCounts]);

  /**
   * work-tasks-performance F1/F2 — ОДНО решение «кто сейчас питает экран», принятое до
   * всех производных. Раньше каждая производная (строки, справочник проектов, денорм
   * статусов, имена компаний) сама смотрела на `hasFilter`, и производные могли
   * разъехаться с источником строк: колонки «Проект» и «Статус» показывали прочерки.
   *
   * `undefined` (данных нет) намеренно отличается от `[]` (пустой ответ) — на этом
   * различии стоит вся дисциплина I1.
   */
  const searchPage = searchQuery.data?.pages?.[0];
  const searchTasksRaw = searchPage ? searchPage.groups.flatMap((g) => g.tasks) : undefined;
  const listTasksRaw = tasksQuery.data?.tasks;
  const listStatus = hasFilter ? searchQuery.status : tasksQuery.status;
  const listError = hasFilter ? searchQuery.error : tasksQuery.error;
  const refetchList = hasFilter ? searchQuery.refetch : tasksQuery.refetch;
  const listIsError = listStatus === 'error';
  const listIsPending = listStatus === 'pending';
  /** Источник строк: поиск при активном фильтре, иначе полная выдача списка. */
  const sourceIsSearch = hasFilter;
  /**
   * F2 (переработано по гейту фазы A) — на смене фильтра показывается СКЕЛЕТОН, а не
   * прошлый набор.
   *
   * Первая редакция строила «мост» между `useTasks` и `useTasksSearch`: пока поиск в
   * полёте, на экране оставались строки нефильтрованного списка с отметкой обновления.
   * Оба ревьюера гейта независимо назвали это блокером: под нажатым чипом «Просрочено»
   * виден набор, который фильтру не соответствует, — «уверенное неверное "вот твои
   * просроченные"» вместо честного «не знаю». Инвариант I1 скелетоном соблюдён:
   * скелетон — это не пустота, а именно «данные едут».
   *
   * `keepPreviousData` (F3) остаётся для операций, НЕ меняющих состав набора по смыслу
   * запроса пользователя (лимит страницы, тумблеры вида) — там прошлые строки помечены
   * устаревшими и массовые действия по ним запрещены.
   */
  const listIsPlaceholder = hasFilter
    ? searchQuery.isPlaceholderData
    : tasksQuery.isPlaceholderData;
  /**
   * Не всякие прошлые данные допустимо показывать. Второй прогон гейта фазы A назвал
   * границу: `keepPreviousData` честен, когда меняется РАЗМЕР или ВИД выдачи (страница,
   * представление), и лжив, когда меняется её СОСТАВ — тумблеры «завершённые»/«архивные»
   * и фильтры относятся ко второму классу («пользователь спросил "покажи архивные" и
   * получает набор без них»). Состав сменился — скелетон, как и на смене фильтра.
   */
  const compositionKey = [
    activeOnly,
    showArchived,
    project ?? '',
    hasFilter,
    // Содержимое фильтра — тоже состав: правка правила при уже активном фильтре меняет
    // набор так же, как его включение, и прошлые строки под новым правилом были бы той
    // же «уверенной неправдой».
    JSON.stringify(activeFilter),
  ].join('|');
  const settledCompositionRef = useRef(compositionKey);
  useEffect(() => {
    if (!listIsPlaceholder) settledCompositionRef.current = compositionKey;
  }, [listIsPlaceholder, compositionKey]);
  const placeholderIsOtherComposition =
    listIsPlaceholder && settledCompositionRef.current !== compositionKey;
  const showSkeleton = listIsPending || placeholderIsOtherComposition;
  // F3 — набор на экране устарел (тот же состав, другой размер): строки прошлые, новые
  // в полёте. Массовые действия по такому набору запрещены — это порча данных.
  const rowsAreStale = listIsPlaceholder && !placeholderIsOtherComposition;
  const loadingIsSlow = useSlowFlag(showSkeleton, 3000);
  // Какие hero-фасеты сейчас нажаты — нужно и для `aria-pressed`, и для честности числа
  // (нажатый фасет = текущий фильтр, его полное число сервер знает точно).
  const mineActive =
    (adhocFilter.assignee_user_ids ?? []).length === 1 &&
    adhocFilter.assignee_user_ids?.[0] === currentUserId;
  const overdueActive = adhocFilter.overdue_only === true;
  const noAssigneeActive = adhocFilter.no_assignee === true;
  const noDateActive = adhocFilter.has_dates === false;
  /**
   * Скелетон обязан занимать столько же места, сколько займут данные: иначе шапка и
   * оговорки прыгают в момент ответа. Считаем от высоты окна по реальной высоте строки
   * (≈39 px) минус шапка приложения, тулбар и зона сообщений.
   */
  const skeletonRows = Math.max(
    8,
    Math.floor(((typeof window === 'undefined' ? 900 : window.innerHeight) - 400) / 39),
  );

  // FE-9 — участники проекта → users-prop пикеров (когда выбран проект).
  const membersQuery = useProjectMembers(project ?? null);
  // go-live 2026-07-03 — без выбранного проекта users был [] → фильтр «Ответственный»
  // (карточка + колонка) и inline-пикеры строк показывали «Нет значений». Глобальный
  // scope-aware пул из /task-module/tasks/assignable-users.
  const globalUsersQuery = useGlobalAssignableUsers(!project);
  const users = project ? (membersQuery.data ?? []) : (globalUsersQuery.data ?? []);

  const projects = projectsQuery.data ?? [];

  // TD-1 — глобальный дэшборд: кросс-проектные states (резолв статус-chip) +
  // projectsById (группировка/проект-chip). Группировка по проекту — default глобально.
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  // BE-3 — имена проектов: основной источник useProjects, плюс fallback на
  // project_directory из ответа (cross-проектные задачи, чей проект не в загруженном
  // списке, всё равно резолвят имя для chip/drill без N+1).
  const projectDirectory = sourceIsSearch
    ? (searchPage?.project_directory ?? [])
    : (tasksQuery.data?.projectDirectory ?? []);
  /**
   * FE-42 — денорм статусов ВСЕХ задач выдачи (BA1 `state_directory`), включая статусы
   * проектов чужой компании: домен их не отдаёт (периметр), а денорм отдаёт, потому что
   * сама задача в периметре. Единственный источник, по которому таблица может показать
   * реальный статус такой задачи вместо прочерка.
   */
  const stateDirectory = sourceIsSearch
    ? (searchPage?.state_directory ?? [])
    : (tasksQuery.data?.stateDirectory ?? []);
  /**
   * F14 — этапы, вырезанные фильтром. Их дети остаются в выдаче и без справочника
   * всплывают корнями: план выглядит рассыпавшимся ровно в тот момент, когда этап
   * отменили (Д-4/UF-3).
   */
  const detachedParents = sourceIsSearch
    ? (searchPage?.detached_parent_directory ?? [])
    : (tasksQuery.data?.detachedParentDirectory ?? []);
  /** FE-42 — state_id → group для доски и таблицы: резолв статусов вне домена. */
  const foreignStateGroups = useMemo(
    () => new Map(stateDirectory.map((s) => [s.state_id, s.group] as const)),
    [stateDirectory],
  );
  const projectsById = useMemo(() => {
    const m = new Map(projects.map((p) => [p.id, p.name] as const));
    for (const d of projectDirectory) if (!m.has(d.project_id)) m.set(d.project_id, d.name);
    return m;
  }, [projects, projectDirectory]);
  // Коды проектов для ПКМ «Копировать → Код»: на глобальном экране у каждой
  // строки свой проект, и без справочника копировался бы голый номер задачи.
  const projectCodeById = useMemo(() => {
    const m = new Map<string, string | null>(projects.map((p) => [p.id, p.code] as const));
    for (const d of projectDirectory) if (!m.has(d.project_id)) m.set(d.project_id, d.code);
    return m;
  }, [projects, projectDirectory]);
  // F13 — мост к существующему поповеру колонок: пункт «Колонки…» в ПКМ по
  // пустому месту открывает ЕГО, а не свою копию списка колонок.
  const columnsOpenRef = useRef<(() => void) | null>(null);
  // B4 — подписки текущего пользователя по выдаче: пункт «Подписаться /
  // Отписаться» в ПКМ обязан показывать текущее состояние.
  const watchedTaskIds = useMemo(
    () => new Set(tasksQuery.data?.watchedTaskIds ?? []),
    [tasksQuery.data],
  );
  // TD-5 — directory user_id→display_name для колонки «Исполнитель» в таблице.
  const assigneeDirectory = useMemo(
    () =>
      new Map((tasksQuery.data?.assigneeDirectory ?? []).map((e) => [e.user_id, e.display_name])),
    [tasksQuery.data?.assigneeDirectory],
  );
  // tasks-by-company F2/F5 — denorm имён компаний + признак агрегата (holding/all).
  // Источник `entities`: list-ответ (без фильтра) или search-ответ (с фильтром).
  const entityNamesById = useMemo(() => {
    const src = sourceIsSearch ? (searchPage?.entities ?? {}) : (tasksQuery.data?.entities ?? {});
    return new Map(Object.entries(src).map(([id, v]) => [id, v.name] as const));
  }, [sourceIsSearch, searchPage, tasksQuery.data?.entities]);
  const activeScope = useActiveEntityStore((s) => s.activeScope);
  const showEntityColumn = activeScope !== 'entity';
  // TD-8-FE-3: `urgency` принимается в URL, но виртуальная корзина срочности —
  // это due-bucket grouping (полноценный dim — TD-14). До тех пор маппим на 'due'.
  // 'urgency' → due-корзина; 'folder' (старые URL/виды) → 'hierarchy' (унификация
  // папок на дерево). FE-18: дефолт Таблицы — плоский список ('none'), а не группировка
  // по проекту; «Проект» доступен и колонкой, и опцией GroupByPopover.
  const effectiveGroup: GroupByValue =
    group === 'urgency' ? 'due' : group === 'folder' ? 'hierarchy' : (group ?? 'none');
  const setGroup = (g: GroupByValue) => {
    // 'none' = default (URL чистый).
    const next = g === 'none' ? undefined : g;
    navigate({ search: (prev) => ({ ...prev, group: next }) });
  };

  // Персистентность вида: навигация «в лоб» на /work-tasks (сайдбар-Link без search,
  // возврат из карточки) чистит URL → вид сбрасывался. Стор хранит последний вид и
  // восстанавливает URL при заходе с чистым search — вид переживает навигацию и reload.
  const persistedView = useWorkTasksViewStore((s) => s.prefs);
  const setPersistedView = useWorkTasksViewStore((s) => s.setPrefs);
  const viewRestoredRef = useRef(false);
  // F23 — Ctrl/⌘+Z отменяет последнюю мгновенную правку (статус, приоритет).
  // Слушатель ставится на весь экран задач: правка делается и из строки, и из
  // ПКМ, и из панели массовых действий — точки входа разные, отмена одна.
  useEffect(() => installTaskUndoHotkey(), []);
  // Персист-эффект ОБЪЯВЛЕН ПЕРВЫМ (до restore) + гейт по viewRestoredRef — не даёт
  // затереть стор «чистым» видом на mount-коммите до восстановления.
  // biome-ignore lint/correctness/useExhaustiveDependencies: пишем актуальный вид на его изменение
  useEffect(() => {
    if (project || !viewRestoredRef.current) return;
    setPersistedView({ group, vt, sort, show_empty, hideprops, colorder, colw, subgroup });
  }, [project, group, vt, sort, show_empty, hideprops, colorder, colw, subgroup]);
  // Восстановление: один раз на mount. Если URL уже несёт вид — он источник правды
  // (синхронизируем стор). Если URL чист, а стор не пуст — восстанавливаем вид в URL.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot на mount / смену project
  useEffect(() => {
    if (project || viewRestoredRef.current) return;
    viewRestoredRef.current = true;
    const urlView: WorkTasksViewPrefs = {
      group,
      vt,
      sort,
      show_empty,
      hideprops,
      colorder,
      colw,
      subgroup,
    };
    if (hasViewPrefs(urlView)) {
      setPersistedView(urlView);
    } else if (hasViewPrefs(persistedView)) {
      // Значения приходят из URL (валидные enum'ы SearchSchema) — каст безопасен.
      const restore = persistedView as Partial<SearchParams>;
      navigate({ search: (prev) => ({ ...prev, ...restore }), replace: true });
    }
  }, [project]);

  const chatFullscreen = useChatPanelStore((st) => st.fullscreen);
  // S17.3 F-C2 — peek через search-param
  const openPeek = (taskId: string) => navigate({ search: (prev) => ({ ...prev, peek: taskId }) });
  const closePeek = () => navigate({ search: (prev) => ({ ...prev, peek: undefined }) });
  /**
   * chat-panel-geometry F-16 [Г5] — под развёрнутым чатом быстрый просмотр не
   * монтируется ВОВСЕ.
   *
   * Не «сжимается по ширине»: арифметика вида `min(…, calc(100vw − … − 100vw))`
   * даёт отрицательную ширину и мусор в левом углу. Панель занимает экран
   * целиком, показывать под ней нечего.
   */
  const peekAside =
    peek && !chatFullscreen ? (
      <TaskPeekPanel
        taskId={peek}
        users={users}
        currentUserId={currentUserId}
        onClose={closePeek}
        onOpenTask={openPeek}
        entityNames={entityNamesById}
        showEntity={showEntityColumn}
      />
    ) : undefined;

  // Project context → полный layout проекта
  if (project) {
    return (
      <ProjectLayoutRoot
        projectId={project}
        users={users}
        onOpenTask={openPeek}
        aside={peekAside}
        zen={zenActive}
        onToggleZen={toggleZen}
      />
    );
  }

  // FE-40 — per-column фильтр: спеки полей (с опциями users/projects) + upsert
  // правила в ТОТ ЖЕ adhocFilter (синхрон с карточкой/чипами, не параллельный стейт).
  const filterFieldByKey = useMemo(() => {
    const base = buildSystemFilterFields();
    const withOpts = base
      // tasks-by-company-fixes — «Компания» в фильтре только в мультикомпанийном охвате
      // (как колонка/группировка). В single-scope — одна компания, фильтр бессмыслен.
      .filter((f) => f.field !== 'entity' || showEntityColumn)
      .map((f) => {
        if (f.field === 'assignee')
          return { ...f, options: users.map((u) => ({ value: u.id, label: u.name })) };
        if (f.field === 'project')
          return { ...f, options: projects.map((p) => ({ value: p.id, label: p.name })) };
        // Опции «Компания» — из denorm-имён всех доступных в scope компаний (BE).
        if (f.field === 'entity')
          return {
            ...f,
            options: [...entityNamesById].map(([id, name]) => ({ value: id, label: name })),
          };
        return f;
      });
    return new Map(withOpts.map((f) => [f.field, f]));
  }, [users, projects, entityNamesById, showEntityColumn]);
  const upsertColumnRule = (
    field: string,
    rule: import('@staiter/shared/task-module').FilterRule | null,
  ) =>
    setAdhocFilter((f) => {
      const rules = (f.rules ?? []).filter((r) => r.field !== field);
      if (rule) rules.push(rule);
      return { ...f, match: f.match ?? 'all', rules };
    });
  const renderColumnFilter = (columnId: string) => {
    const fieldKey = COLUMN_FILTER_FIELD[columnId];
    if (!fieldKey) return null;
    const spec = filterFieldByKey.get(fieldKey);
    if (!spec) return null;
    const rule = adhocFilter.rules?.find((r) => r.field === fieldKey);
    return (
      <ColumnFilterPopover
        spec={spec}
        rule={rule}
        onChange={(r) => upsertColumnRule(fieldKey, r)}
      />
    );
  };

  const visibleOfTotal = searchPage?.visible_of_total;
  const rbacBlocked =
    hasFilter && visibleOfTotal && visibleOfTotal[0] === 0 && visibleOfTotal[1] > 0;

  const searchTasks = hasFilter ? (searchTasksRaw ?? []) : [];
  const tasks = (sourceIsSearch ? searchTasksRaw : listTasksRaw) ?? [];
  // work-tasks-defaults F4 — плоская таблица показывает первые pageSize (клиентский слайс
  // ПОЛНОГО набора); hero/группировка/гант/доска/календарь используют полный `tasks` (счётчики точны).
  // work-tasks-performance F2 — во время моста слайс тоже действует: иначе клик по
  // hero-чипу на секунду разворачивал в DOM полторы тысячи строк вместо ста.
  const tableTasks = sourceIsSearch ? tasks : tasks.slice(0, pageSize);
  /**
   * F18 — подводка выполняется ПОСЛЕ того, как строки приехали, и ровно один
   * раз на запись из чата (`version`). Эффект живёт здесь, а не в панели: панель
   * не знает, вернулся ли запрос, а поиск строки по DOM из чужого поддерева был
   * бы гаданием.
   */
  useEffect(() => {
    if (chatWriteVersion === 0 || chatWriteVersion === revealedChatVersion.current) return;
    if (tableTasks.length === 0) return;
    const api = chatRevealApi.current;
    if (!api) return;
    revealedChatVersion.current = chatWriteVersion;
    const first = tableTasks[0];
    // Фокус остаётся в чате: человек продолжает читать карточку [Х15].
    if (first) api.scrollToTask(first.id, { focus: false });
    const column = filterSurface.revealColumn;
    if (column) api.revealColumn(column);
  }, [chatWriteVersion, tableTasks, filterSurface.revealColumn]);
  const tableDisplaySliced = !sourceIsSearch && tasks.length > pageSize;
  /**
   * Раунд 2 гейта захода 8 (UX, 🔴 BLOCKER) — «Показано N» называет то, что НА
   * ЭКРАНЕ.
   *
   * Дословно: «Кадр 2 одновременно утверждает „Показано 2000 из 2068“, „50“ в
   * селекторе и „6 на странице“ в заголовке … вышли три базы на одном экране».
   * Плашка печатала длину СКАЧАННОГО набора, а таблица показывала первые
   * `pageSize` строк из него — то есть 2000 против пятидесяти на самом деле
   * видимых. Число теперь одно и то же с тем, что можно пересчитать глазами.
   */
  const shownRowCount = displayView === 'table' ? tableTasks.length : tasks.length;
  /**
   * F95 (§9.6, TCA-F4) — id показанных строк для «Не на экране»: таблица — срез страницы,
   * доска/гант/календарь — весь набор. Панели по-прежнему идёт полный `tasks` (К3-26).
   */
  // С-G Р.9 (акт С-F) — доска и календарь сами сообщают, что показывают: у календаря — даты
  // показанного периода, у доски — всё, кроме свёрнутых дорожек. Гант показывает весь набор.
  const [layoutVisible, setLayoutVisible] = useState<{
    view: string;
    ids: ReadonlySet<string>;
  } | null>(null);
  const onBoardVisible = useCallback(
    (ids: ReadonlySet<string>) => setLayoutVisible({ view: 'board', ids }),
    [],
  );
  const onCalendarVisible = useCallback(
    (ids: ReadonlySet<string>) => setLayoutVisible({ view: 'calendar', ids }),
    [],
  );
  const visibleTaskIds = useMemo(() => {
    if (displayView === 'table') return new Set(tableTasks.map((t) => t.id));
    if (layoutVisible && layoutVisible.view === displayView) return layoutVisible.ids;
    return new Set(tasks.map((t) => t.id));
  }, [displayView, tableTasks, tasks, layoutVisible]);

  // Связи для общего ганта: проекты всех задач канваса (включая вторичные членства
  // M2M — задача может стоять строкой в нескольких лейнах) одним батч-запросом.
  // Раньше сюда уезжал жёсткий `dependencies={[]}` — стрелок на общем ганте не было
  // вовсе, а кнопка «Критический путь» вечно оставалась серой.
  const ganttProjectIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of tasks) {
      if (t.project_ids.length > 0) for (const pid of t.project_ids) ids.add(pid);
      else if (t.project_id) ids.add(t.project_id);
    }
    return [...ids].sort();
  }, [tasks]);

  /**
   * FE-42 — домен статусов строится по проектам СПИСКА **и** проектам ЗАДАЧ выдачи.
   *
   * Раньше он шёл только по `useProjects()`, а это проекты активной компании. Задача же
   * проходит по своему `entity_id`, и её проект может принадлежать другой компании
   * холдинга (кросс-компанийное членство — на проде так живут задачи ECOTEC в проектах
   * InsulTech). Статус такой задачи в домен не попадал, и `groupByState` клал её в
   * первую попавшуюся колонку чужого проекта — молча и правдоподобно.
   *
   * Цена — второй запрос домена после догрузки задач (ключ меняется). Это по-прежнему
   * один запрос на набор, а не по одному на проект.
   */
  const stateDomainProjectIds = useMemo(() => {
    const ids = new Set(projectIds);
    for (const pid of ganttProjectIds) ids.add(pid);
    return [...ids].sort();
  }, [projectIds, ganttProjectIds]);
  const statesQuery = useStatesForProjects(stateDomainProjectIds);
  const globalStates = statesQuery.data;
  const ganttDepsQuery = useQuery({
    queryKey: ['task-module', 'dependencies-batch', ganttProjectIds],
    queryFn: () => fetchDependenciesByProjects(ganttProjectIds),
    enabled: displayView === 'gantt' && ganttProjectIds.length > 0,
    staleTime: 30_000,
  });

  // FE-21 / BE-5 — домен меток. Тянем только когда ось «По метке» активна: на
  // остальных видах это лишний запрос, а не «на всякий случай».
  const labelsQuery = useQuery({
    queryKey: ['task-module', 'labels', ganttProjectIds],
    queryFn: () => fetchLabels({ projectIds: ganttProjectIds, withUsage: true }),
    enabled: group === 'label',
    staleTime: 60_000,
  });
  const labelDomain = labelsQuery.data?.labels;

  // Пак context-menu (F4) — один сборщик меню на весь экран задач: он же
  // обслуживает таблицу, доску, календарь и гант. Строки и карточки получают
  // готовую функцию, потому что состав bulk-меню знает только вид целиком.
  const surfaceMenu = useTaskSurfaceMenu({
    surface:
      displayView === 'board'
        ? 'kanban'
        : displayView === 'calendar'
          ? 'calendar'
          : displayView === 'gantt'
            ? 'gantt'
            : 'table',
    tasks,
    states: globalStates ?? [],
    users,
    projectIds: [...projectsById.keys()],
    projectCodeById,
    watchedTaskIds,
    hasMoveTargets: projects.length > 1,
    onOpenPeek: openPeek,
  });

  // task-quick-capture F2 — автофокус inline-поля при появлении пустого состояния (ref —
  // biome a11y запрещает декларативный autoFocus). Things-паттерн: курсор сразу готов.
  const tasksEmpty = tasks.length === 0;
  // work-tasks-performance F1 — пустота ПОДТВЕРЖДЁННАЯ: пока запрос в полёте или упал,
  // ноль строк ничего не значит. Только по этому признаку показывается EmptyState и
  // уводится фокус в quick-add (иначе фокус крали у пользователя на каждой загрузке).
  const tasksConfirmedEmpty = listStatus === 'success' && tasksEmpty;
  useEffect(() => {
    if (tasksConfirmedEmpty) quickAddRef.current?.focus();
  }, [tasksConfirmedEmpty]);
  // FE-35 — превью результата фильтра (total_count из search, иначе размер списка).
  const resultCount = hasFilter ? (searchPage?.total_count ?? tasks.length) : tasks.length;

  // FE-38 — честность выдачи. `total_count` считает БД по тем же фильтрам, а `tasks` —
  // то, что реально доехало (лимит списка 100, кап ганта 2000). Если они расходятся,
  // экран обязан это сказать: иначе счётчики групп и «Без дат · N» выдают длину
  // обрезанного массива за полное число задач — уверенно и неверно.
  // F14 — в постраничном режиме собственный COUNT списка не запрашивается
  // (`include_total=false`), и «сколько всего» знает только ручка счётчиков.
  const totalAvailable = hasFilter
    ? (searchPage?.total_count ?? tasks.length)
    : (serverCounts?.total ?? tasksQuery.data?.totalCount ?? tasks.length);
  /**
   * С-G Р.9 (акт С-F) — найденный набор для подписи «Добавить / Заменить» у «Выбрать все
   * найденные»: исход замены зависит от того, лежит ли всё выделенное в НАЙДЕННОМ наборе, а не
   * среди загруженных строк. Загружен весь набор — он и есть найденный; иначе id найденных
   * догружаются (`ids_only`) только когда часть выделения вне загруженного.
   */
  const foundFilter = useMemo(() => {
    const f = activeOnly
      ? { ...activeFilter, state_groups: [...ACTIVE_STATE_GROUPS] }
      : activeFilter;
    return { ...f, ...(project ? { project_ids: [project] } : {}) };
  }, [activeOnly, activeFilter, project]);
  const loadedIdSet = useMemo(() => new Set(tasks.map((t) => t.id)), [tasks]);
  const allFoundLoaded = tasks.length >= totalAvailable;
  const needFoundIds =
    !allFoundLoaded &&
    totalAvailable > selectedIdsNow.size &&
    [...selectedIdsNow].some((id) => !loadedIdSet.has(id));
  const foundIdsQuery = useQuery({
    queryKey: ['tasks', 'found-ids', foundFilter],
    queryFn: () => fetchAllTaskIds(foundFilter),
    enabled: needFoundIds,
    staleTime: 30_000,
  });
  const foundIds = useMemo(
    () =>
      allFoundLoaded
        ? loadedIdSet
        : foundIdsQuery.data
          ? new Set<string>(foundIdsQuery.data)
          : undefined,
    [allFoundLoaded, loadedIdSet, foundIdsQuery.data],
  );
  // Флаг обрезки берётся у ТОГО источника, который питает экран. Раньше `truncated`
  // списка (кап 2000) продолжал действовать и в режиме поиска: экран заявлял «показаны
  // первые 401 из 401 — счётчики неполны», хотя поиск отдал набор целиком.
  const isTruncated = sourceIsSearch
    ? tasks.length < totalAvailable
    : (tasksQuery.data?.truncated ?? false) || tasks.length < totalAvailable;
  // NC-29 (F13 tails-closeout) — действия полосы обрезки по её режиму.
  const truncActions = truncationActions({
    tablePagedMode,
    pageSize,
    maxPageSize: MAX_PAGE_SIZE,
    grouped: group !== 'none',
  });
  /**
   * FND-17 — есть ли следующая страница.
   *
   * Источник — `next_cursor` САМОЙ выдачи, а не арифметика по `total`: счётчик
   * приходит из другой ручки и под другим набором фильтров, и «страница ×
   * номер < total» на границе давало бы кнопку, ведущую в пустоту.
   */
  const nextCursor = sourceIsSearch ? null : (tasksQuery.data?.nextCursor ?? null);
  const pageHasNext = nextCursor !== null;
  /**
   * Раунд 2 гейта (UI-2, UX-2) — знаменатель и диапазон строк.
   *
   * «Страница 1» без знаменателя не отвечает на вопрос, ради которого пейджер и
   * заводился: где я в выдаче и сколько ещё. Число страниц считается от полного
   * `totalAvailable` и размера страницы, а НЕ от числа доехавших строк: последняя
   * страница короче, и деление по ней дало бы завышенный счёт.
   */
  const pageNumber = cursorStack.length + 1;
  const pageCount = Math.max(1, Math.ceil(totalAvailable / Math.max(1, pageSize)));
  const rowsFrom = totalAvailable === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const rowsTo = Math.min(totalAvailable, (pageNumber - 1) * pageSize + tasks.length);
  /**
   * Пейджер — ОДНО определение на два места (над таблицей и под ней, UX-3).
   *
   * Копия внизу разъехалась бы с верхней на первой же правке — ровно тот класс,
   * который свип чинит в другом месте (две матрицы умолчаний уведомлений).
   */
  const pager =
    tablePagedMode && (cursorStack.length > 0 || pageHasNext) ? (
      <nav
        /*
         * Раунд 4 гейта: резерв под панель массовых действий снят с пейджера.
         * Он остался ровно один — на теле таблицы, которое панель и накрывает;
         * здесь он сдвигал пейджер вверх на 44 px относительно соседей по
         * полосе подвала (замер: 1049 против 1093) — то есть сам и создавал ту
         * «вторую базовую линию», которую гейт называл дважды.
         */
        className={styles.pager}
        aria-label={m.tm_page_nav_label()}
      >
        <button
          type="button"
          className={styles.pagerBtn}
          disabled={cursorStack.length === 0}
          onClick={() => setCursorStack((prev) => prev.slice(0, -1))}
        >
          {m.tm_page_prev()}
        </button>
        <span className={styles.pagerPosition}>
          {m.tm_page_position({ page: pageNumber, pages: pageCount })}
          {' · '}
          {m.tm_page_rows_range({ from: rowsFrom, to: rowsTo, total: totalAvailable })}
        </span>
        {/*
          Раунд 3 гейта, UX-2: правило «переход снимает выделение» было верным, но
          НЕОБЪЯВЛЕННЫМ — узнать его можно было только потеряв работу. Строка
          появляется ровно тогда, когда есть что терять.
        */}
        <button
          type="button"
          className={styles.pagerBtn}
          disabled={!pageHasNext}
          onClick={() => setCursorStack((prev) => (nextCursor ? [...prev, nextCursor] : prev))}
        >
          {m.tm_page_next()}
        </button>
      </nav>
    ) : null;

  /**
   * Оговорка о потере выделения — СВОЕЙ строкой под полосой подвала.
   *
   * Раунд 4 гейта захода 8, обе половины: «Подвал под выделением разъехался на
   * пять базовых линий». Причина — вторая строка внутри грида пейджера: она
   * делала его вдвое выше соседей, и выравнивание полосы по центру разносило
   * элементы по высоте ровно в том состоянии, где подвал и нужен. Оговорка
   * осталась у ДЕЙСТВИЯ, которое приводит к потере (кнопки «Назад»/«Дальше»
   * прямо над ней), но перестала распирать саму полосу.
   *
   * Прежний повод держать её внутри грида (раунд 4 гейта FND-17: «стоя между
   * кнопками, оно выталкивало „Дальше“ на следующую строку») сохранён — между
   * кнопками её нет и здесь.
   */
  // F44 (H3) — оговорка о потере выделения снята: выделение больше не теряется.
  const pagerWarning = null;

  /**
   * Пропсы числа hero-чипа. Нажатый фасет — это и есть текущий фильтр, и его полное
   * число сервер вернул точным (`total_count`). Показывать рядом «≥ 200» и полосу
   * «первые 200 из 401» — прямое противоречие: два числа об одном множестве (третий
   * прогон гейта фазы A). Ненажатые чипы под капом честно дают нижнюю границу.
   */
  const heroNumber = (value: number, pressed: boolean) =>
    // При упавшем запросе счётчик неизвестен. Ноль — валидное бизнес-значение
    // («просроченных нет»), и печатать его поверх ошибки значит врать числом
    // (седьмой прогон гейта фазы A).
    listIsError
      ? { value: 0, pending: false, unknown: true }
      : pressed && isTruncated
        ? { value: totalAvailable, pending: showSkeleton, stale: rowsAreStale }
        : {
            value,
            pending: showSkeleton,
            stale: rowsAreStale,
            // F14 — «≥» ставится, только когда число посчитано ПО СТРАНИЦЕ.
            // С серверными счётчиками (B10) оно точное, и нижняя граница
            // рядом с точным числом читалась бы как «мы не уверены».
            atLeast: isTruncated && serverCounts === undefined,
          };

  const saveSlot = (
    <SaveViewButton
      state={{
        vt: displayView,
        group: effectiveGroup,
        sort: sortBy,
        hideprops: [...hiddenProps],
        filter: adhocFilter,
      }}
      isDefault={!hasFilter}
    />
  );

  // TD-1 — Hero-метрики (Level 0): risk-рид руководителя, кросс-проектно.
  // Overdue считаем ТОЙ ЖЕ slaInfo-логикой, что и бейдж в строке (консистентность чисел).
  // F94 (§9.5): «сегодня» и «неделя вперёд» — деловой день из `/auth/me`, не UTC.
  const todayStr = businessTodayIso();
  const weekAhead = addIsoDays(todayStr, 7);
  //
  // work-tasks-performance F14 — числа берутся у СЕРВЕРА (`/tasks/counts`, B10),
  // когда он их посчитал. Клиентский подсчёт остаётся запасным путём для случая
  // «фильтр активен» (там счётчиков нет) и на время, пока ручка отвечает: считать
  // по загруженной СТРАНИЦЕ и называть это общим числом — то самое «уверенно и
  // неверно», ради чего пак и делается.
  //
  // work-tasks-performance F17 — пять фильтров по набору стали ОДНИМ проходом, и
  // тот выполняется только когда счётчиков сервера нет. Раньше все пять шли на
  // каждом рендере (в том числе на наведении мыши и на открытии поповера), а их
  // результат тут же выбрасывался ветками `??`, если сервер уже ответил.
  const pageHeroCounts = useMemo(() => {
    if (serverCounts) return null;
    let overdue = 0;
    let dueSoon = 0;
    let unassigned = 0;
    let mine = 0;
    let noDate = 0;
    for (const t of tasks) {
      const group = stateGroupOf(t.state_id, { states: globalStates ?? null });
      const sla = slaInfo(t.due_date, t.completed_at, group, {
        kind: t.kind,
        todayIso: todayStr,
      });
      if (sla.state === 'overdue' || sla.state === 'severe') overdue += 1;
      // С-G Р.7 — «срок на неделе» как у сервера (`/tasks/counts`, `overdueEligibleStateSql`):
      // активная группа статуса, а не пустой `completed_at` (приостановленная и отменённая
      // в счёт не идут).
      const active = group !== null && isOverdueEligible(group as StateGroup);
      if (active && t.due_date && t.due_date >= todayStr && t.due_date <= weekAhead) dueSoon += 1;
      if (t.assignees.length === 0) unassigned += 1;
      if (currentUserId && t.assignees.some((a) => 'user_id' in a && a.user_id === currentUserId))
        mine += 1;
      if (!t.start_date && !t.due_date) noDate += 1;
    }
    return { overdue, dueSoon, unassigned, mine, noDate };
  }, [serverCounts, tasks, todayStr, weekAhead, currentUserId, globalStates]);
  const overdueCount = serverCounts?.hero.overdue ?? pageHeroCounts?.overdue ?? 0;
  const weekCount = serverCounts?.hero.due_soon ?? pageHeroCounts?.dueSoon ?? 0;
  const unassignedCount = serverCounts?.hero.unassigned ?? pageHeroCounts?.unassigned ?? 0;
  // work-tasks-defaults F5/F6 — счётчики быстрых чипов.
  const myCount = serverCounts?.hero.mine ?? pageHeroCounts?.mine ?? 0;
  const noDateCount = serverCounts?.hero.no_date ?? pageHeroCounts?.noDate ?? 0;

  // TD-2 — Доска/Календарь кросс-проектно: projectId по умолчанию для create-flow.
  const firstProjectId = projects[0]?.id ?? tasks[0]?.project_id ?? '';

  // FE-18/FE-19 — единственный резолв группировки. Раньше здесь стоял молчаливый
  // откат: ось вне списка досок падала в 'priority', и пользователь, выбрав
  // «По проекту», получал колонки приоритетов без единого слова об этом.
  const grouping = resolveGrouping({
    view: VIEW_TO_TASK_VIEW[displayView],
    scope: activeScope === 'entity' ? 'entity' : activeScope === 'all' ? 'all' : 'holding',
    group: effectiveGroup as GroupKey,
    ...(subgroup ? { subgroup: subgroup as GroupKey } : {}),
  });
  const resolvedGroup = grouping.dim.key as GroupByValue;
  // Доска знает ось «группа статусов» как 'state' + флаг stateGroupMode (шесть
  // фиксированных колонок вместо N×статусов всех проектов) — сводим к её словарю.
  const kanbanGroup = (resolvedGroup === 'state_group' ? 'state' : resolvedGroup) as KanbanGroupBy;

  // F13 — меню пустого места. «Создать задачу» открывает тот же быстрый ввод,
  // что Ctrl+Shift+K; «Свернуть все группы» — тот же URL-механизм, что заголовки
  // групп. Копий контролов вида здесь нет.
  const buildBlankMenu = useBlankAreaMenu({
    onCreateTask: () => openQuickAdd(),
    grouping: {
      value: resolvedGroup,
      options: grouping.options,
      onChange: (next) => setGroup(next as GroupByValue),
    },
    onOpenColumns: () => columnsOpenRef.current?.(),
    ...(resolvedGroup === 'none'
      ? {}
      : {
          onCollapseAll: () => {
            // Ключи групп берём у того же измерения, что строит группы в видах
            // (`keysOf` из реестра измерений), а не собираем вторым правилом.
            const keys = new Set<string>();
            for (const t of tasks) for (const k of grouping.dim.keysOf(t)) keys.add(k);
            void navigate({
              search: (prev) => ({ ...prev, collapsed: [...keys].join(',') || undefined }),
            });
          },
        }),
  });

  // FE-17/FE-19 — календарь не перегруппировывает, он КРАСИТ плашки. Список
  // «красящих» измерений жил здесь отдельным массивом COLOR_DIMS — теперь он в
  // реестре, и календарь берёт из него подмножество, а не заводит своё правило.
  const calendarColorOptions = CALENDAR_COLOR_KEYS.map((k) => {
    const d = dimensionOf(k);
    return { key: d.key, label: d.label(), disabled: false };
  });
  // Тип сужен до «красящих» ключей: календарь физически не умеет ничего другого,
  // и широкий GroupByValue здесь только маскировал бы несовместимость.
  const calendarColorKey: 'state' | 'priority' | 'label' = CALENDAR_COLOR_KEYS.includes(
    effectiveGroup as GroupKey,
  )
    ? (effectiveGroup as 'state' | 'priority' | 'label')
    : 'state';

  // FE-25 — на многозначной оси (метка, исполнитель, проект) одна задача даёт несколько
  // строк. Считаем перебор строк над задачами: если он есть — шапка обязана это сказать.
  const rowsOverTasks = useMemo(() => {
    if (!resolvedGroup || resolvedGroup === 'none') return 0;
    const dim = grouping.dim;
    if (dim.cardinality !== 'many') return 0;
    /**
     * Гейт фазы D, раунд 5 (UI): подпись обещала «503 строки на 500 задач», а
     * таблица рисовала ровно 500 — и счётчик положения внизу это подтверждал.
     *
     * Причина: реестр измерений считает ось «проект» многозначной (`keysOf` =
     * `project_ids`), а `groupByProject` кладёт задачу под ОДИН проект, главный.
     * Проекции реально возникают только там, где их делает сам селектор группы —
     * на метках и исполнителях. Для остальных осей подпись обещала строки,
     * которых на экране нет.
     */
    if (resolvedGroup !== 'label' && resolvedGroup !== 'assignee') return 0;
    let extra = 0;
    for (const t of tasks) {
      const n = dim.keysOf(t).length;
      if (n > 1) extra += n - 1;
    }
    return extra;
  }, [grouping.dim, resolvedGroup, tasks]);

  // FE-34 — CTA «Сохранить как вид»: текущий стейт тулбара → SavedView.
  /**
   * work-tasks-performance F1 — ЕДИНАЯ зона сообщений о данных с зарезервированной
   * высотой. Раньше каждая оговорка рождалась после ответа и толкала чипы и шапку
   * таблицы вниз на свою высоту (замер повторного гейта фазы A: ~51 px). Теперь зона
   * объявлена во всех состояниях и держит место, а по §4.1b стоит первой строкой
   * контентной области — выше фильтров (пятый прогон гейта).
   *
   * Круг 20 кадра 4 гейта F26 (UX-1, дословно): «Пустой резерв оговорок в
   * full-bleed поверхности — резерв вводился под таблицу, где оговорка реальна;
   * на ганте она невозможна by design, поэтому 56px+гэпы держат пустоту в
   * инструменте, которому SSOT велит отдавать вертикаль. Направление узкое:
   * резервировать по виду, а не по маршруту — вид, порождающий оговорку,
   * резервирует; гант не резервирует».
   *
   * Резерв остался ровно там, где оговорка возможна. Гант резерв не держит: если
   * говорить нечего, зоны нет вовсе; если есть (обрезка выдачи, обновление) —
   * зона появляется со своим содержимым.
   */
  const reservesNotice = displayView !== 'gantt';
  const noticeHasContent =
    showSkeleton ||
    rowsAreStale ||
    isTruncated ||
    rowsOverTasks > 0 ||
    (displayView === 'table' && tableDisplaySliced);
  const noticeZone =
    !reservesNotice && !noticeHasContent ? null : (
      <div
        className={[
          styles.noticeZone,
          // §4.1b — оговорка о выдаче идёт ПЕРВОЙ строкой контента. В разметке
          // она объявлена после пилюли процесса (чтобы условия читались рядом),
          // порядок отображения задаёт flex.
          styles.noticeZoneOrdered,
          rowsAreStale ? styles.noticeZoneStale : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Что сейчас происходит с данными. Тон info с точкой-пульсом отличает
                процесс от page-level оговорок ниже — те про выдачу. */}
        {showSkeleton || rowsAreStale ? (
          <div className={styles.refreshBadge} role="status">
            <RefreshCw size={13} aria-hidden="true" className={styles.refreshIcon} />
            {rowsAreStale
              ? m.tm_list_refreshing()
              : loadingIsSlow
                ? m.tm_list_loading_long()
                : m.tm_list_loading()}
          </div>
        ) : reservesNotice ? (
          // Слот процесса тоже держит место: без него исчезновение пилюли после
          // ответа поднимало всё содержимое на её высоту.
          <div className={styles.refreshBadgeGhost} aria-hidden="true" />
        ) : null}
        {/* FE-38 — выдача обрезана: экран говорит об этом прямо. Молчать нельзя —
                счётчики групп и «Без дат · N» считаются по тому, что доехало, и без этой
                строки выдают неполную картину за полную.

          Гейт фазы D, раунд 1 (оба ревьюера, BLOCKER): полоса стояла ПОСЛЕ строки
          «N задач · M строк» и рядом с hero-числами по всей выдаче — область
          действия каждого числа была неочевидна, а сама полоса была тупиком без
          действия. Теперь она идёт первой, называет порядок строк (без него
          «первые 500» — произвольное окно) и несёт действие. Тон — info: обрезка
          большого списка это штатная жизнь, а не отклонение (§6.1). */}
        {isTruncated && (
          /*
          Раунд 4 гейта, UX-5: в ПОСТРАНИЧНОМ режиме «показано 500 из 1472» — это
          не деградация выдачи, а размер страницы, и янтарный грейд на штатном
          состоянии притупляет реакцию на настоящие. Warn остаётся там, где обрезка
          действительно означает неполноту (гант, поиск, группировка без страниц).
        */
          <div className={tablePagedMode ? styles.pagedBadge : styles.truncatedBadge} role="status">
            {/* Глиф несёт ТОТ ЖЕ статус, что и цвет (§1.3): контурный круг
              предупреждения — без тревожной массы треугольника, но и не нейтральная
              справка. */}
            {tablePagedMode ? (
              <Info size={14} aria-hidden="true" />
            ) : (
              <AlertCircle size={14} aria-hidden="true" />
            )}
            {/*
            WTU-FND-G — одно число не живёт в двух местах.

            В постраничном режиме положение в наборе называет ПЕЙДЖЕР («Страница 1
            из 42 · строки 1–50 из 2068»), и повторять «Показано 50 из 2068» здесь
            значит ставить два утверждения об одной выдаче на разные концы экрана:
            при расхождении (а оно возможно — плашка считает доехавшие строки,
            пейджер знает набор) непонятно, какое верно. Плашка остаётся при том,
            чего пейджер не говорит, — порядке сортировки.

            Вне постраничного режима пейджера нет, и подпись здесь единственная:
            там она остаётся полной.
          */}
            {/* Раунд 5 гейта: сообщение — слева, действия и подсказка одним хвостом
              справа. Текст поэтому в своём элементе: `margin-inline-end: auto`
              нельзя повесить на голый текстовый узел. */}
            <span className={styles.noticeText}>
              {tablePagedMode
                ? m.tm_order_only({ order: SORT_LABEL[sortBy]() })
                : serverCounts
                  ? m.tm_truncated_page_order({
                      shown: shownRowCount,
                      total: totalAvailable,
                      order: SORT_LABEL[sortBy](),
                    })
                  : m.tm_truncated_badge({ shown: shownRowCount, total: totalAvailable })}
            </span>
            {/*
            Раунд 1 гейта, UX-1: когда постраничность недоступна (группировка с
            фильтром), человек упирался в «сузьте фильтр» БЕЗ объяснения — пейджер
            просто пропадал. Молчание здесь хуже отсутствия функции: выглядит как
            то, что путей больше нет вовсе.
          */}
            {displayView === 'table' && !tablePagedMode && (
              <span className={styles.noticeHint}>{m.tm_page_grouped_unavailable()}</span>
            )}
            {/*
            Раунд 6 гейта (UI, MAJOR): «Действия разорваны прозой — контейнер
            хвоста не разделяет „текст“ и „кнопки“». Обе ссылки съезжают в ОДНУ
            группу в конце полосы, а объяснение остаётся прозой без вкраплённого
            в него действия. Прежняя редакция держала «Снять группировку» внутри
            предложения — оно и читалось хвостом пояснения, а не выходом.
          */}
            {displayView === 'table' &&
              (truncActions.showMaxPage || truncActions.showDropGrouping) && (
                <span className={styles.noticeActions}>
                  {/* Больше 500 страница не бывает — тогда действия нет, и обещать
                    его нечем: путь к остальному лежит через фильтр.
                    NC-29 (F13 tails-closeout): только в постраничном режиме — вне его
                    «Показать 500» уменьшало видимый срез (см. truncation-actions.ts). */}
                  {truncActions.showMaxPage && (
                    <button
                      type="button"
                      className={styles.noticeAction}
                      onClick={() => setPageSize(MAX_PAGE_SIZE)}
                    >
                      {m.tm_show_max_page()}
                    </button>
                  )}
                  {/* Объяснение без выхода — тупик: причина названа, и рядом стоит
                    ровно то действие, которое её снимает. */}
                  {truncActions.showDropGrouping && (
                    <button
                      type="button"
                      className={styles.noticeAction}
                      onClick={() => navigate({ search: (prev) => ({ ...prev, group: 'none' }) })}
                    >
                      {m.tm_page_drop_grouping()}
                    </button>
                  )}
                </span>
              )}
          </div>
        )}
        {/*
        FND-17 — выход к остальным задачам. До пейджера полоса была тупиком:
        «показано 500 из 1472», страница уже максимальна, и путь к оставшейся
        тысяче лежал только через сужение фильтра. Ручка курсор умела с самого
        начала — не было потребителя.

        РАУНД 2 ГЕЙТА — что изменилось против первой редакции и почему:
        • навигация ВЫНЕСЕНА из строки-оговорки в собственный `<nav>` (UI-1/UI-4,
          UX-1). Внутри `role="status"` она переозвучивалась целиком на каждой
          смене страницы, а warn-грейд оговорки красил постоянный контрол —
          §1.3 отдаёт семантический цвет только отклонениям, и пагинация,
          которая есть всегда, обесценивала `--warn` как сигнал;
        • у номера появился ЗНАМЕНАТЕЛЬ и диапазон строк (UI-2, UX-2): «Страница
          1 из 3 · строки 1–500 из 1472». «Страница 1» без знаменателя не
          отвечала на вопрос, ради которого пейджер и заводился, — где я в выдаче;
        • пейджер продублирован ПОД таблицей (UX-3): навигация нужна там, где
          заканчивается чтение, а не только над пятьюстами строками.
      */}
        {/* FE-25 — многозначная ось: задача с тремя метками даёт три строки. Без этой
                подписи сумма счётчиков групп выглядит как ошибка подсчёта. */}
        {rowsOverTasks > 0 && (
          <div className={styles.rowsBadge} role="status">
            {m.tm_tasks_rows_count({
              tasks: tasks.length,
              rows: tasks.length + rowsOverTasks,
            })}
          </div>
        )}
        {/* work-tasks-defaults F4 — плоская таблица показывает первые pageSize из
                ПОЛНОГО набора: счётчики полны (не «неполны»), это просто display-слайс. */}
        {!isTruncated && displayView === 'table' && tableDisplaySliced && (
          <div className={styles.rowsBadge} role="status">
            {/* F24 — «значок + текст» (§4.1b): без значка строка про полноту выдачи
              читается как обычная подпись таблицы и теряется. */}
            <Info size={14} aria-hidden="true" />
            {m.tm_page_shown_plain({ shown: pageSize, total: tasks.length })}
            {/* NC-13 (свод tails-sweep-2, заход 5): было «— увеличьте размер
              страницы, чтобы увидеть остальные». Инструкция вместо действия:
              продукт знает и сколько строк не показано, и где лежит рычаг, но
              просит пользователя сходить за ним самому — в селект под таблицей.
              Рычаг тем же приёмом, что у СОСЕДНЕЙ полосы (обрезка выдачи): там
              кнопка «Показать 500» стоит с прошлого гейта, а здесь осталась
              инструкция. Один аффорданс — одно поведение, поэтому и шаг тот же
              (максимум страницы), и класс тот же. */}
            {pageSize < MAX_PAGE_SIZE && (
              <button
                type="button"
                className={styles.noticeAction}
                onClick={() => setPageSize(MAX_PAGE_SIZE)}
              >
                {m.tm_page_shown_action({ count: MAX_PAGE_SIZE })}
              </button>
            )}
          </div>
        )}
        {/* Место под оговорку о выдаче держится всегда: до ответа мы не знаем,
                упрётся ли набор в кап, и её появление сдвигало чипы и шапку таблицы
                вниз на свою высоту (третий прогон гейта фазы A). */}
        {reservesNotice && !isTruncated && !(displayView === 'table' && tableDisplaySliced) && (
          <div className={styles.rowsBadge} aria-hidden="true">
            &nbsp;
          </div>
        )}
      </div>
    );

  // FE-29 — подписи дорожек ганта. Без них заголовок показал бы сырой ключ группы
  // (`assignee:<uuid>`, `label:<uuid>`) или «Без проекта» на оси «метка».
  const ganttLaneLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, name] of projectsById) map.set(id, name);
    for (const l of labelDomain ?? []) map.set(`label:${l.id}`, l.name);
    for (const u of users) {
      map.set(`assignee:${u.id}`, u.name);
      map.set(`assignee:u:${u.id}`, u.name);
      map.set(`author:${u.id}`, u.name);
    }
    map.set('assignee:none', m.tm_no_assignees());
    map.set('label:none', m.tm_group_no_label());
    for (const s of globalStates) map.set(s.id, s.name);
    return map;
  }, [projectsById, labelDomain, users, globalStates]);

  // FE-20 — откат ВИДИМЫЙ: показываем причину и вычищаем мёртвый параметр из URL.
  // Раньше несовместимая ось молча подменялась ('priority' на доске, 'state' в
  // проекте, 'none' в таблице), ссылка продолжала обещать группировку, которой нет.
  const notice = grouping.notices[0];
  const noticeKey = notice ? `${notice.requested}:${displayView}` : null;
  const lastNoticeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!notice || !noticeKey || lastNoticeRef.current === noticeKey) return;
    lastNoticeRef.current = noticeKey;
    emitToast({
      variant: 'info',
      title: m.tm_group_fallback_notice({
        dim: dimensionOf(notice.requested).label(),
        reason: notice.reason,
      }),
    });
    navigate({ search: (prev) => ({ ...prev, group: undefined }) });
  }, [notice, noticeKey, navigate]);

  /*
   * F51, круг 2 гейта «Таблица задач» (UI и UX, MAJOR): настройки того, ЧТО видно в
   * таблице — завершённые/архивные, группировка и сортировка, колонки, — стояли в
   * шапке страницы тремя строками выше фильтра, и «Колонки» переносились отдельной
   * строкой. Это одна задача с поиском и фильтром, поэтому живут они в одной строке
   * над таблицей (слот `right` FilterBar). В шапке остаются заголовок, охват
   * компаний и переключатель представлений.
   */
  const viewControls = (
    <>
      {/* work-tasks-defaults F2 — тумблер «Показывать завершённые»: снимает скрытие
          закрытых (completed+cancelled). Общий с проектной поверхностью (один pref). */}
      <button
        type="button"
        className={[styles.viewBtn, showClosed ? styles.viewActive : ''].join(' ')}
        aria-pressed={showClosed}
        onClick={() => setShowClosed(!showClosed)}
        title={m.tm_show_closed()}
      >
        {showClosed ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />}
        {/* Кадр 3 дизайн-гейта F26, круг 12 (UI-1): при открытой панели шапка
            не помещалась в строку и переносилась, съедая высоту у данных.
            Круг 13 (UX-1): «тумблеры состава выдачи потеряли и подпись, и
            индикацию состояния — человек может анализировать усечённую выдачу,
            не зная об этом». Поэтому подпись не ПРЯЧЕТСЯ, а КОРОТЧАЕТ: по
            ширине самой шапки «Показывать завершённые» становится
            «Завершённые». Слово остаётся, ряд помещается. */}
        <span className={styles.viewBtnLabel}>{m.tm_show_closed()}</span>
        <span className={styles.viewBtnLabelShort}>{m.tm_show_closed_short()}</span>
      </button>
      {/*
        F14, раунд 3 гейта (UX) — счётчик вырезанных фильтром этапов.

        Крошка в дереве объясняет ОДИН этап; сколько их всего и где снять срез —
        вопрос уровня всей выдачи, поэтому действие стоит у фильтра, а не под
        каждой крошкой.
      */}
      {!showClosed && detachedParents.length > 0 && effectiveGroup === 'hierarchy' && (
        <button
          type="button"
          className={[styles.viewBtn, styles.detachedBtn].join(' ')}
          onClick={() => setShowClosed(true)}
          aria-label={m.tm_detached_parents_hidden_aria({ count: detachedParents.length })}
          title={m.tm_detached_parents_hidden_title()}
        >
          <FolderOpen size={14} aria-hidden />
          {m.tm_detached_parents_hidden({ count: detachedParents.length })}
        </button>
      )}
      {/* F18 — тумблер «Показывать архивные». Рядом с «завершёнными», но
          отдельной кнопкой: закрытая задача — результат работы, архивная —
          убранная запись. */}
      <button
        type="button"
        className={[styles.viewBtn, showArchived ? styles.viewActive : ''].join(' ')}
        aria-pressed={showArchived}
        onClick={() => setShowArchived(!showArchived)}
        title={m.tm_show_archived()}
      >
        {/* Кадр 3 гейта F26, круг 14, обе половины: «„Архивные“ не сообщает
            состояние ничем, кроме пустой заливки — иконка коробки называет
            сущность, а не состояние, тогда как у соседа иконка
            „перечёркнутый глаз“ состояние несёт». Пара значков у обоих
            тумблеров одна и та же: видно / не видно. */}
        {showArchived ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />}
        <span className={styles.viewBtnLabel}>{m.tm_show_archived()}</span>
        <span className={styles.viewBtnLabelShort}>{m.tm_show_archived_short()}</span>
      </button>
      {/*
        work-tasks-defaults F4 + WTU-FND-E — размер страницы живёт ПОД таблицей,
        рядом с её номером, и не появляется здесь ни в каком режиме.

        Раунд 2 гейта захода 8 назвал первую редакцию правки дословно:
        «размер страницы живёт в трёх точках и мигрирует между режимами …
        собственное правило „дублировать нельзя“ не выдержано». Первая
        редакция переносила контрол в пейджер только в постраничном режиме —
        то есть он ПЕРЕЕЗЖАЛ, и найти его дважды подряд можно было в разных
        местах. Теперь место одно и всегда одно: подвал таблицы.
      */}
      {/* FE-29 — у ганта появился тот же поповер группировки: он больше не
          игнорирует ось. Раньше на вопрос «сгруппируй гант по исполнителю» ответа
          не было вовсе — контрола не существовало. */}
      {displayView === 'gantt' && (
        <GroupByPopover value={resolvedGroup} onChange={setGroup} options={grouping.options} />
      )}
      {(displayView === 'table' || displayView === 'board') && (
        <DisplayPanel
          group={resolvedGroup}
          onGroupChange={setGroup}
          groupOptions={grouping.options}
          sort={sortBy}
          onSortChange={setSortBy}
          showEmpty={showEmpty}
          onShowEmptyChange={setShowEmpty}
          {...(grouping.dim.hasDomain ? {} : { showEmptyDisabledReason: m.tm_group_none() })}
          showSort={displayView !== 'board'}
          {...(displayView === 'table'
            ? {
                properties: tableProperties,
                hiddenProps,
                onToggleProperty: toggleProperty,
              }
            : {})}
          {...(displayView === 'board' && resolvedGroup !== 'none'
            ? { subGroup: subgroup ?? 'none', onSubGroupChange: setSubGroup }
            : {})}
        />
      )}
      {/* FE-17 — единый GroupByPopover на кросс-проектном календаре:
          цветовая маркировка плашек (статус/приоритет/метка). */}
      {displayView === 'calendar' && (
        <GroupByPopover
          value={calendarColorKey}
          onChange={setGroup}
          options={calendarColorOptions}
          colorMode
        />
      )}
      {displayView === 'table' && (
        <ColumnConfigPopover
          columns={columnDescriptors}
          visible={visibleColumnIds}
          onToggle={toggleProperty}
          onReorder={setColumnOrder}
          onReset={resetColumns}
          scopeLabel={m.tm_route_all_tasks()}
          openRef={columnsOpenRef}
        />
      )}
    </>
  );
  return (
    <WorkspaceSurface
      aside={peekAside}
      zen={zenActive}
      header={
        <>
          <h1 className={styles.title}>{m.tm_route_tasks_heading()}</h1>
          {/* tasks-by-company-fixes F-1/F-2 — общий двунаправленный scope-контрол:
              single+мульти → CTA «Все компании»; агрегат → индикатор «· N» + × возврат. */}
          <ScopeAllCompaniesCta count={entityNamesById.size} />
          <div
            className={styles.viewSwitcher}
            role="radiogroup"
            aria-label={m.tm_route_view_switcher()}
          >
            {VIEW_TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={displayView === key}
                className={[styles.viewBtn, displayView === key ? styles.viewActive : ''].join(' ')}
                onClick={() => setDisplayView(key)}
                // FND-15 — прогрев кэша целевого вида на наведении и на фокусе.
                // Таблица и гант просят РАЗНЫЕ ключи (страница против полного
                // набора), поэтому переключение неизбежно показывало скелетон:
                // данные едут только после клика. Наведение даёт им фору.
                onPointerEnter={() => prefetchView(key)}
                onFocus={() => prefetchView(key)}
              >
                <Icon size={14} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          <div className={styles.headerSpacer} />
          {/* F51, круг 2 (обе половины гейта): настройки вида — в строке фильтра над
              таблицей; в шапке они остаются только в zen, где строки фильтра нет. */}
          {zenActive && viewControls}
        </>
      }
    >
      <div className={`${styles.inboxLayout} ${zenActive ? styles.inboxLayoutZen : ''}`}>
        <div className={styles.inboxMain}>
          {/* §4.1b — оговорка о выдаче стоит ПЕРВОЙ строкой контентной области, выше
              фильтров: иначе фильтр настраивают раньше, чем читают, что выдача обрезана
              (пятый прогон гейта фазы A). Здесь же живёт пилюля процесса. */}
          {!zenActive && noticeZone}
          {/* Группа П (§6) — в zen прячем фильтры/чипы/бейджи/KPI: остаётся только гант-блок. */}
          {!zenActive && (
            <FilterBar
              externalVersion={filterSurface.version}
              rules={rulesState}
              onRulesChange={onRulesChange}
              searchText={adhocFilter.q ?? ''}
              onSearchText={setSearchText}
              users={users}
              projects={projects}
              entityOptions={
                showEntityColumn ? [...entityNamesById].map(([id, name]) => ({ id, name })) : []
              }
              currentUserId={currentUserId}
              activeViewId={activeViewId}
              dirty={viewDirty}
              currentFilter={adhocFilter}
              onSelectView={applyView}
              onClearView={clearView}
              resultCount={resultCount}
              saveSlot={saveSlot}
              right={viewControls}
            />
          )}
          {/* F4 [Л8] — чужое сужение выборки обязано быть видно: «задач нет»
              при незамеченном фильтре читается как факт о работе. */}
          {!zenActive && filterSurface.fromChat && (
            <ChatFilterChip
              onReset={resetChatFilter}
              {...(filterSurface.filter.task_ids
                ? {
                    taskIdsCount: filterSurface.filter.task_ids.length,
                    // F18 — сколько строк состава реально на экране: страница
                    // бывает короче состава, и молчаливое усечение здесь — ложь
                    // ровно в момент проверки перед необратимым действием.
                    shownCount: tableTasks.length,
                  }
                : {})}
            />
          )}
          {!zenActive && (
            <ActiveFilterChips
              filter={adhocFilter}
              onChange={setAdhocFilter}
              users={users}
              projects={projects}
              onReset={clearView}
            />
          )}
          {/* task-quick-capture design-review (UI+UX consensus): при пустой компании
              (нет фильтра + 0 задач) hero-чипы «Просрочено 0 / Срок 0 / Без исполнителя 0»
              — мёртвый шум над единственным CTA-полем. Скрываем до первой задачи. */}
          {/* work-tasks-performance F1 — во время загрузки ряд hero-чипов РЕНДЕРИТСЯ со
              скелетонами вместо чисел: иначе он появлялся после ответа и сдвигал таблицу
              вниз на свою высоту (layout jump на каждом заходе). */}
          {!zenActive && (listIsPending || !(tasksEmpty && !hasFilter)) && (
            <div
              className={[styles.heroRow, rowsAreStale ? styles.heroRowStale : '']
                .filter(Boolean)
                .join(' ')}
            >
              {/* work-tasks-defaults F5 — «Мои задачи» (только назначенные мне). Гвард: без
                  currentUserId чип не кликабелен (иначе assignee_user_ids=[''] → показал бы всех).
                  work-tasks-performance F1 — но во время загрузки он всё равно занимает место:
                  профиль приезжает отдельным запросом, и без этого ряд чипов дёргался вбок. */}
              {(currentUserId || listIsPending) && (
                <button
                  type="button"
                  className={styles.heroMetric}
                  disabled={!currentUserId}
                  aria-pressed={mineActive}
                  onClick={() =>
                    setAdhocFilter((f) => {
                      const mine =
                        (f.assignee_user_ids ?? []).length === 1 &&
                        f.assignee_user_ids?.[0] === currentUserId;
                      const { assignee_user_ids: _a, ...rest } = f;
                      return mine ? rest : { ...f, assignee_user_ids: [currentUserId] };
                    })
                  }
                >
                  <HeroValue {...heroNumber(myCount, mineActive)} />
                  <span className={styles.heroLabel}>{m.tm_route_my_tasks()}</span>
                </button>
              )}
              <button
                type="button"
                className={styles.heroMetric}
                aria-pressed={overdueActive}
                onClick={() =>
                  setAdhocFilter((f) => ({
                    ...f,
                    overdue_only: !f.overdue_only ? true : undefined,
                  }))
                }
              >
                <HeroValue
                  {...heroNumber(overdueCount, overdueActive)}
                  {...(overdueCount > 0 ? { tone: styles.heroDanger } : {})}
                />
                <span className={styles.heroLabel}>{m.tm_overdue()}</span>
              </button>
              <button
                type="button"
                className={styles.heroMetric}
                aria-pressed={adhocFilter.due_from === todayStr && adhocFilter.due_to === weekAhead}
                onClick={() =>
                  setAdhocFilter((f) =>
                    f.due_from === todayStr && f.due_to === weekAhead
                      ? (({ due_from: _df, due_to: _dt, ...rest }) => rest)(f)
                      : { ...f, due_from: todayStr, due_to: weekAhead },
                  )
                }
              >
                <HeroValue
                  {...heroNumber(
                    weekCount,
                    adhocFilter.due_from === todayStr && adhocFilter.due_to === weekAhead,
                  )}
                />
                <span className={styles.heroLabel}>{m.tm_route_due_7d()}</span>
              </button>
              <button
                type="button"
                className={styles.heroMetric}
                aria-pressed={noAssigneeActive}
                onClick={() =>
                  setAdhocFilter((f) => ({ ...f, no_assignee: !f.no_assignee ? true : undefined }))
                }
              >
                <HeroValue
                  {...heroNumber(unassignedCount, noAssigneeActive)}
                  {...(unassignedCount > 0 ? { tone: styles.heroWarn } : {})}
                />
                <span className={styles.heroLabel}>{m.tm_route_no_assignees()}</span>
              </button>
              {/* work-tasks-defaults F6 — «Без срока» (has_dates=false) для быстрой правки. */}
              <button
                type="button"
                className={styles.heroMetric}
                aria-pressed={noDateActive}
                onClick={() =>
                  setAdhocFilter((f) =>
                    f.has_dates === false
                      ? (({ has_dates: _hd, ...rest }) => rest)(f)
                      : { ...f, has_dates: false },
                  )
                }
              >
                <HeroValue {...heroNumber(noDateCount, noDateActive)} />
                <span className={styles.heroLabel}>{m.tm_route_no_due()}</span>
              </button>
              {/* Гейт фазы D (оба ревьюера, BLOCKER): чипы считает сервер по ВСЕЙ
                  выдаче, а под ними лежит страница. «916 без ответственных» над
                  пятьюстами строками читалось как ошибка, пока область числа не
                  названа рядом с самим числом, а не в отдельной оговорке. */}
              {serverCounts && isTruncated && (
                <span className={styles.heroScope}>{m.tm_counts_scope_all()}</span>
              )}
            </div>
          )}
          {rbacBlocked ? (
            <SavedViewEmptyRBAC
              totalCount={visibleOfTotal ? visibleOfTotal[1] : 0}
              onOpenFilters={() => {
                setAdhocFilter({});
                navigate({ search: { group } });
              }}
            />
          ) : listIsError ? (
            // work-tasks-performance F1 (I1) — упавший запрос перестаёт выглядеть пустотой.
            // Раньше 500-ка от бэкенда давала ровно тот же экран «Нет активных задач», и
            // пользователь искал свои задачи вместо того, чтобы нажать «Повторить».
            <ErrorState
              error={(listError as Error | null) ?? new Error('list')}
              title={m.tm_list_error_title()}
              retryLabel={m.tm_list_error_retry()}
              onRetry={() => void refetchList()}
            />
          ) : showSkeleton && displayView !== 'table' ? (
            // Таблица получает настоящий скелетон строк (примитив `Table`), остальные виды
            // — общий плейсхолдер: своя геометрия, скелетон-строки в ней врали бы о макете.
            <SkeletonTable rows={8} cols={5} />
          ) : tasksConfirmedEmpty ? (
            // Задачи живут внутри проектов: на пустой компании даём явный CTA —
            // «Создать проект» (нет проектов) либо «Открыть проекты» (есть, но без задач).
            // work-tasks-defaults F2 — если закрытые скрыты (activeOnly) и пусто, честный
            // заголовок «Нет активных задач» + CTA «Показать завершённые» (не «создать»).
            <EmptyState
              title={activeOnly && !hasFilter ? m.tm_no_active_tasks() : m.tasks_empty_title()}
              description={m.tasks_quickadd_inbox_hint()}
              action={
                <div className={styles.quickAddWrap}>
                  {activeOnly && !hasFilter && (
                    <button
                      type="button"
                      className={styles.quickAddProjectLink}
                      onClick={() => setShowClosed(true)}
                    >
                      {m.tm_show_closed()}
                    </button>
                  )}
                  {/* task-quick-capture F2 — готовое поле с автофокусом: печатай → Enter создаёт
                      задачу во «Входящих» и открывает доску инбокса. Кнопка проекта вторична. */}
                  <input
                    ref={quickAddRef}
                    type="text"
                    className={styles.quickAddInput}
                    placeholder={m.tasks_quickadd_placeholder()}
                    value={quickTitle}
                    disabled={quickAddMut.isPending}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && quickTitle.trim()) {
                        e.preventDefault();
                        quickAddMut.mutate(quickTitle.trim());
                      } else if (e.key === 'Escape') {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    aria-label={m.tasks_quickadd_placeholder()}
                  />
                  <button
                    type="button"
                    className={styles.quickAddProjectLink}
                    onClick={() =>
                      navigate({ to: projects.length === 0 ? '/projects/new' : '/projects' })
                    }
                  >
                    {projects.length === 0 ? m.tasks_create_project() : m.tasks_open_projects()}
                  </button>
                </div>
              }
            />
          ) : displayView === 'table' ? (
            <>
              <GlobalTableLayout
                tasks={tableTasks}
                chatRevealApi={chatRevealApi}
                loading={showSkeleton}
                loadingRows={skeletonRows}
                stale={rowsAreStale}
                states={globalStates}
                // FE-42 — денорм статусов ответа (BA1). Периметр компании не даёт домену
                // отдать статус проекта ЧУЖОЙ компании (и правильно делает), а денорм его
                // содержит: задача-то в периметре пользователя. Без этого пропа таблица
                // резолвить чужой статус не может — отсюда прочерк в колонке «Статус».
                stateDirectory={stateDirectory}
                detachedParents={detachedParents}
                projectsById={projectsById}
                assigneeDirectory={assigneeDirectory}
                onOpenTask={openPeek}
                group={effectiveGroup}
                collapsedGroups={collapsedGroups}
                onToggleGroup={toggleGroup}
                showEmptyGroups={showEmpty}
                hiddenProps={hiddenProps}
                columnOrder={columnOrder}
                columnWidths={columnWidths}
                onColumnResize={setColumnWidth}
                renderColumnFilter={renderColumnFilter}
                entityNamesById={entityNamesById}
                showEntityColumn={showEntityColumn}
                buildContextMenu={surfaceMenu.buildForTask}
                buildBlankMenu={buildBlankMenu}
                {...(groupTotals ? { groupTotals } : {})}
                totalInSet={totalAvailable}
                // Раунд 2 гейта: подпись окна прокрутки считает в той же нумерации,
                // что и пейджер, — иначе на экране два знаменателя одной выдачи.
                pageOffset={tablePagedMode ? (pageNumber - 1) * pageSize : 0}
                // Раунд 1 гейта хвоста FND: подпись положения и плашка над
                // таблицей называли РАЗНЫЕ знаменатели одной выдачи («из 100» и
                // «из 1472»). Знаменатель один — полный набор.
                totalCount={totalAvailable}
                // На экране первые `pageSize` из полного набора: об этом уже
                // говорит плашка над таблицей, и второе утверждение о той же
                // выдаче внизу читалось бы как спор с ней (раунд 2 гейта).
                displaySliced={tableDisplaySliced}
                pagedMode={tablePagedMode}
                /**
                 * WTU-FND-I — знаменатель заголовков групп.
                 *
                 * Таблица показывает срез первых `pageSize` строк, а считать для
                 * знаменателя надо весь набор, который экран уже скачал. Отдаём
                 * НАБОР, а не посчитанные числа: считать их здесь значило бы завести
                 * вторую копию правила группировки, и она разошлась бы с той, по
                 * которой строятся строки.
                 *
                 * И только когда набор ПОЛНЫЙ. Полная выдача обрезается на двух
                 * тысячах, и на обрезанной «6 из 816» означало бы «из того, что
                 * доехало» — та же подмена базы, от которой уходим. При обрезке
                 * заголовок остаётся при честной пометке «на странице», а про
                 * обрезку говорит плашка над таблицей.
                 */
                {...(isTruncated ? {} : { tasksInSet: tasks })}
                /*
                 * Раунд 3 гейта: подвал собирается ОДНОЙ полосой внутри таблицы.
                 * Поверхность отдаёт свою половину — размер страницы и пейджер;
                 * переход к строке таблица рисует сама (она знает номера строк).
                 */
                footerSlot={
                  <>
                    {/*
                      Раунд 4 гейта: подпись ИНЛАЙН, а не над контролом — иначе
                      блок вдвое выше соседей по полосе, и общей базовой линии
                      нет. Видимую подпись рисует сосед, доступное имя —
                      `aria-label` контрола: два текста подряд в дереве
                      доступности читались бы дважды.
                    */}
                    <span className={styles.footerSize}>
                      <span aria-hidden="true">{m.tm_page_size_label()}</span>
                      <Select
                        label={m.tm_page_size_label()}
                        hideLabel
                        aria-label={m.tm_page_size_label()}
                        size="sm"
                        value={String(pageSize)}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        options={PAGE_SIZE_OPTIONS.map((n) => ({
                          value: String(n),
                          label: String(n),
                        }))}
                      />
                    </span>
                    {pager}
                    {pagerWarning}
                  </>
                }
                // Выход из тупика «нет на этой странице»: сузить экран до проекта.
                // Тем же фильтром, что и карточка фильтров, — второго механизма
                // отбора на экране быть не должно.
                onFocusGroup={(groupKey) =>
                  setAdhocFilter((f) => ({
                    ...f,
                    match: f.match ?? 'all',
                    rules: [
                      ...(f.rules ?? []).filter((r) => r.field !== 'project'),
                      { field: 'project', operator: 'eq' as const, value: groupKey },
                    ],
                  }))
                }
              />
            </>
          ) : displayView === 'board' ? (
            <KanbanLayout
              buildContextMenu={surfaceMenu.buildForTask}
              buildBlankMenu={buildBlankMenu}
              projectId={firstProjectId}
              tasks={tasks}
              states={globalStates}
              users={users}
              assigneeDirectory={assigneeDirectory}
              groupBy={kanbanGroup}
              // FE-42 — денорм статусов выдачи. Страница проекта его передавала, общий
              // дашборд — нет: задача из проекта чужой компании (её статуса в домене нет,
              // периметр) уезжала в колонку «Запланировано», хотя реально была в работе.
              foreignStateGroups={foreignStateGroups}
              // 92 (M2M) — кросс-проектная доска: колонки = 6 фиксированных групп,
              // drag → bulk set_state_group (BB13 ремапит в primary-workflow).
              stateGroupMode
              projectNamesById={projectsById}
              // FE-21 — домены осей: без них «показывать пустые группы» было пустышкой,
              // а колонка неиспользованной метки не появлялась вовсе.
              labelDomain={labelDomain}
              showEmptyGroups={showEmpty}
              {...(subgroup && subgroup !== 'none' && subgroup !== kanbanGroup
                ? {
                    subGroupBy: subgroup as KanbanGroupBy,
                    collapsedSubGroups,
                    onToggleSubGroup: toggleSubGroup,
                  }
                : {})}
              onVisibleIdsChange={onBoardVisible}
              onOpenTask={openPeek}
            />
          ) : displayView === 'calendar' ? (
            <Suspense fallback={<div className={styles.empty} />}>
              <CalendarLayout
                projectId={firstProjectId}
                tasks={tasks}
                states={globalStates}
                users={users}
                onOpenTask={openPeek}
                // FE-19 — календарь красит, а не группирует (см. calendarColorKey).
                groupBy={calendarColorKey}
                onVisibleIdsChange={onCalendarVisible}
              />
            </Suspense>
          ) : (
            <Suspense fallback={<div className={styles.empty} />}>
              <GanttSurface
                tasks={tasks}
                dependencies={ganttDepsQuery.data ?? []}
                states={globalStates}
                users={users}
                onOpenTask={openPeek}
                projectsById={projectsById}
                // FE-29 — гант получает ОСЬ, а не булев флаг «свимлейны по проектам».
                // Раньше он игнорировал группировку целиком: «в ганте группировок нет».
                groupBy={resolvedGroup === 'none' ? 'project' : resolvedGroup}
                groupLabels={ganttLaneLabels}
                // REWORK-2 R2-0f (C0) — тот же per-column фильтр, что в «Таблице»
                // (иконка на заголовке → ColumnFilterPopover → общий adhocFilter).
                renderColumnFilter={renderColumnFilter}
                entityNamesById={entityNamesById}
                zen={zenActive}
                onToggleZen={toggleZen}
              />
            </Suspense>
          )}
        </div>
      </div>
      {/* bulk-edit D2/D3 (2026-07-09) — users для «Ответственный» (был скрыт пустым
          пропом), projects для «Проект» (в «Все компании» — с группировкой по
          компаниям: компания задачи следует за проектом). */}
      {/* F7 — диалоги, которые открывают пункты меню (перенос, даты, второй проект). */}
      <TaskMenuHost />
      {/* work-tasks-performance F3 — на устаревшем наборе бар не появляется: выделить по
          нему нечего (чекбоксы недоступны), а показанный бар обещал бы массовую правку
          по списку, которого через мгновение не будет. */}
      {!rowsAreStale && (
        <BulkEditBar
          /**
           * F19 [Х21] — пока выделением распоряжается предложение чата, панель
           * сворачивается: два пути изменить одни и те же задачи (через
           * черновик с аудитом и мимо него) — худшее, что можно предложить.
           */
          {...(chatComposition.draftId
            ? {
                compositionMode: {
                  k: [...selectedIdsForComposition].filter((id) => chatCompositionIds.has(id))
                    .length,
                  n: chatComposition.taskIds.length,
                },
              }
            : {})}
          // F20 — стейты всех проектов вида; применимость к выделению решает
          // `resolveStatusChoice`, а не пустой проп (раньше бар всегда показывал
          // группы, даже когда выделение целиком из одного проекта).
          states={globalStates ?? []}
          users={users}
          tasks={tasks}
          visibleIds={visibleTaskIds}
          projects={projects.map((p) => ({
            id: p.id,
            name: p.name,
            company_name: entityNamesById.get(p.entity_id) ?? null,
          }))}
          allowedActionIds={surfaceMenu.bulkActionIds}
          paged={tablePagedMode}
          foundTotal={totalAvailable}
          foundIds={foundIds}
          onSelectAllFound={async () => {
            // F45 (WTU-FND-D4): тот же отбор, что у экрана, — id всех найденных.
            const ids = foundIdsQuery.data ?? (await fetchAllTaskIds(foundFilter));
            const projectById = new Map(tasks.map((t) => [t.id, t.project_id]));
            selectAllTasks(ids.map((id) => ({ id, projectId: projectById.get(id) ?? '' })));
          }}
          showingSelected={showSelectedIds !== null}
          onToggleShowSelected={() =>
            setShowSelectedIds((cur) => (cur ? null : [...selectedIdsNow]))
          }
        />
      )}
    </WorkspaceSurface>
  );
}
